// Mode check that tolerates the shared resolver in snippets/global-js.liquid not
// having run yet. Quantity mode is the safe default here: it is what the section
// rendered before tag mode existed, and it never depends on per-card tag data.
function isClearanceTagModeSafe() {
  return typeof isClearanceTagMode === 'function' ? isClearanceTagMode() : false
}
if (!customElements.get('clearance-v3-flow')) {
  // Caps the Deals pack carousel: at most this many pack cards are ever visible, counting the
  // curated metaobject packs and the bundles derived from the Choose style rows together. Also
  // the number of slots the load skeleton reserves, so the row never changes width mid-load.
  // The server-rendered placeholder loop must be kept in step with it.
  const PACK_ROW_TARGET_COUNT = 6

  // Both horizontally scrolling tracks — the style sub-rows and the Quick Bundles carousel —
  // are driven by one set of arrow helpers, which find a track from its wrapper and back again
  // through these. Each wrapper holds exactly one track plus its prev/next arrows.
  const SCROLL_TRACK_SELECTOR =
    '[data-clearance-v3-style-row-scroller], [data-clearance-v3-scroll-track]'
  const SCROLL_TRACK_WRAP_SELECTOR = '.sub-row-body, .pack-row-body'

  class ClearanceV3Flow extends HTMLElement {
    constructor() {
      super()
      this.selectedTopSize = ''
      this.selectedBottomWaist = ''
      this.selectedBottomLength = ''
      // Letter-sized bottoms (XSM–4XLG), separate from waist/length.
      this.selectedBottomSize = ''
      // Left unset so no Tops Length button starts preselected; filtering treats an
      // unset value as 'Regular' via getEffectiveTopLength().
      this.selectedTopLength = ''
      this.activeTab = 'deals'
      this.singlesAbortController = null
      this.singlesObserver = null
      this.singlesNextPage = 0
      this.isLoadingMoreSingles = false
      // Pick & Mix is rendered in full from the singles JSON, then revealed a page at a
      // time. The cap counts *matching* cards (post size/length filter) so every page is
      // a full 30 regardless of how much the filter excludes.
      this.singlesPageSize = 30
      this.singlesShownCount = 30
      this.hasMoreSingles = false
      this.singlesDomObserver = null
      this.clearanceCount = 0
      this.clearanceSubtotal = 0
      this.cartVariantIds = new Set()
      this.cartVariantQuantities = new Map()
      this.cardIdCounter = 0
      this.tiers = []
      this.baseDiscount = 0
      this.handleSizeClick = this.handleSizeClick.bind(this)
      this.handleWaistClick = this.handleWaistClick.bind(this)
      this.handleLengthClick = this.handleLengthClick.bind(this)
      this.handleTopLengthClick = this.handleTopLengthClick.bind(this)
      this.handleBottomSizeClick = this.handleBottomSizeClick.bind(this)
      this.handleShowDeals = this.handleShowDeals.bind(this)
      this.handleTabClick = this.handleTabClick.bind(this)
      // A/B test concluded: this.handleShopAllBundles = this.handleShopAllBundles.bind(this)
      this.handleChangeSize = this.handleChangeSize.bind(this)
      this.handleViewCart = this.handleViewCart.bind(this)
      this.handlePackAdd = this.handlePackAdd.bind(this)
      this.handleSingleAdd = this.handleSingleAdd.bind(this)
      this.handleCartRefresh = this.refreshCartSummary.bind(this)
      this.handleQuickAddSuccess = this.handleQuickAddSuccess.bind(this)
      this.handleModalSizeClick = this.handleModalSizeClick.bind(this)
      this.handleModalWaistClick = this.handleModalWaistClick.bind(this)
      this.handleModalLengthClick = this.handleModalLengthClick.bind(this)
      this.handleModalTopLengthClick = this.handleModalTopLengthClick.bind(this)
      this.handleModalApply = this.applyModalSize.bind(this)
      this.handleCloseModal = this.closeSizeModal.bind(this)
      this.handleSinglesIntersection = this.handleSinglesIntersection.bind(this)
      this.handleSinglesDomIntersection = this.handleSinglesDomIntersection.bind(this)
      this.handleStyleCardClick = this.handleStyleCardClick.bind(this)
      this.handleStyleBack = this.handleStyleBack.bind(this)
      this.handleStyleSubNavClick = this.handleStyleSubNavClick.bind(this)
      this.handleScrollArrowClick = this.handleScrollArrowClick.bind(this)
      this.handleCarouselScroll = this.handleCarouselScroll.bind(this)
      this.handleCarouselResize = this.handleCarouselResize.bind(this)
      this.handleHeaderOffsetResize = this.syncHeaderOffset.bind(this)
    }

    connectedCallback() {
      this.hero = this.querySelector('#hero')
      this.productsArea = this.querySelector('#productsArea')
      this.browseHeader = this.querySelector('.browse-header')
      this.stepOne = this.querySelector('[data-clearance-v3-step-one]')
      this.stepTwo = this.querySelector('[data-clearance-v3-step-two]')
      this.sizeCard = this.querySelector('[data-clearance-v3-size-card]')
      this.sizeButtons = Array.from(this.querySelectorAll('[data-clearance-v3-size-option]'))
      this.showDealsButton = this.querySelector('[data-clearance-v3-show-deals]')
      this.changeSizeButton = this.querySelector('[data-clearance-v3-change-size]')
      this.viewCartButton = this.querySelector('[data-clearance-v3-view-cart]')
      this.selectedSizeLabel = this.querySelector('[data-clearance-v3-selected-size-label]')
      this.tabButtons = Array.from(this.querySelectorAll('[data-clearance-v3-tab-button]'))
      this.dealsView = this.querySelector('[data-clearance-v3-deals-view]')
      /* A/B test concluded: automatic bundles DOM references retained for reference.
      this.automaticBundlesGrid = this.querySelector(
        '[data-clearance-v3-automatic-bundles-grid]'
      )
      this.automaticBundlesLoading = this.querySelector(
        '[data-clearance-v3-automatic-bundles-loading]'
      )
      this.automaticBundlesEmpty = this.querySelector(
        '[data-clearance-v3-automatic-bundles-empty]'
      )
      this.automaticBundlesShopAll = this.querySelector(
        '[data-clearance-v3-automatic-bundles-shop-all]'
      )
      */
      this.allStyleViews = Array.from(this.querySelectorAll('[data-clearance-v3-style-view]'))
      this._setActiveStyleTab('mens')
      this.styleCards = Array.from(this.querySelectorAll('[data-clearance-v3-style-card]'))
      // Choose style fallbacks (documentation/clearance-v3-style-fallbacks.md). The row
      // template is cloned now, while every server-rendered row is still empty.
      this.styleFallbacks = this.readStyleFallbackSettings()
      this.styleFallbackRowTemplate = this.isAnyStyleFallbackEnabled()
        ? this.cloneStyleFallbackRowTemplate()
        : null
      // Assume a fetch is coming whenever there's a collection to fetch, so the singles
      // grid's empty state stays suppressed until fetchAndRenderSingles() resolves.
      this.isFetchingSingles = !!this.dataset.clearanceCollection

      // Resolved ahead of renderPackCards: it inserts cards relative to the server-rendered
      // skeleton placeholders, and the template has to be cloned off one of them before
      // syncPackPlaceholders starts trimming them away.
      this.packSection = this.querySelector('[data-clearance-v3-pack-section]')
      this.packRow = this.packSection?.querySelector('.pack-row') || null
      this.packPlaceholderTemplate =
        this.packRow
          ?.querySelector('[data-clearance-v3-pack-placeholder]')
          ?.cloneNode(true) || null

      const _productsData = this.parseAllProducts()
      if (_productsData && _productsData.packs && _productsData.packs.length) {
        this.renderPackCards(_productsData.packs)
      }

      this.refreshPackCardBindings()
      this.singleCards = Array.from(this.querySelectorAll('[data-clearance-v3-single-card]'))
      for (const card of this.singleCards) this.stripLengthIfNotEligibleForLengthFilter(card)
      this.addSingleButtons = Array.from(this.querySelectorAll('[data-clearance-v3-add-single]'))
      this.countElement = this.querySelector('[data-clearance-v3-count]')
      this.messageElement = this.querySelector('[data-clearance-v3-message]')
      this.stickyFill = this.querySelector('[data-clearance-v3-sticky-fill]')
      this.stickyBar = this.querySelector('[data-clearance-v3-sticky-bar]')
      this.singlesCountElement = this.querySelector('[data-clearance-v3-singles-count]')
      this.singlesGrid = this.querySelector('[data-clearance-v3-singles-grid]')
      this.singlesLoading = this.querySelector('[data-clearance-v3-singles-loading]')
      this.singlesSkeleton = this.querySelector('[data-clearance-v3-singles-skeleton]')
      this.singlesSentinel = this.querySelector('[data-clearance-v3-singles-sentinel]')
      // Always 0: the grid is filled from the Storefront API by fetchAndRenderSingles, which
      // walks the whole collection itself, so there is never a server-rendered next page to
      // fetch. The section no longer paginates, so there's no data-next-page to read either.
      // This keeps loadMoreSingles/fetchSinglesPage inert — see maybeRevealMoreSingles for the
      // DOM-reveal path that actually drives infinite scroll.
      this.singlesNextPage = 0
      this.emptyElement = this.querySelector('[data-clearance-v3-empty]')
      this.sizeModal = this.querySelector('[data-clearance-v3-size-modal]')
      this.sizeModalOverlay = this.querySelector('[data-clearance-v3-modal-overlay]')
      this.modalSizeButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-size-option]')
      )
      this.modalWaistButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-waist-option]')
      )
      this.modalLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-length-option]')
      )
      this.modalTopLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-top-length-option]')
      )
      this.modalApplyButton = this.querySelector('[data-clearance-v3-modal-apply]')
      this.modalCloseButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-close]')
      )
      this.modalBottomsSection = this.querySelector('.sz-modal-bottoms-size')
      this.toastWrap = this.querySelector('[data-clearance-v3-toast-wrap]')
      this.bottomCard = this.querySelector('[data-clearance-v3-bottom-card]')
      this.waistButtons = Array.from(this.querySelectorAll('[data-clearance-v3-waist-option]'))
      this.lengthButtons = Array.from(this.querySelectorAll('[data-clearance-v3-length-option]'))
      this.topLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-top-length-option]')
      )
      // Step 1 and modal Bottoms rows share one handler and stay in lockstep.
      this.bottomSizeButtons = Array.from(
        this.querySelectorAll('[data-tc-bottom-size-option], [data-tc-modal-bottom-size-option]')
      )
      this.tiers = this.parseTiers()
      this.baseDiscount = this.parseBaseDiscount()
      this.clearanceVariantIds = this.buildClearanceVariantIds()

      for (const button of this.sizeButtons) {
        button.addEventListener('click', this.handleSizeClick)
      }

      for (const button of this.waistButtons) {
        button.addEventListener('click', this.handleWaistClick)
      }

      for (const button of this.lengthButtons) {
        button.addEventListener('click', this.handleLengthClick)
      }

      for (const button of this.topLengthButtons) {
        button.addEventListener('click', this.handleTopLengthClick)
      }

      for (const button of this.bottomSizeButtons) {
        button.addEventListener('click', this.handleBottomSizeClick)
      }

      for (const button of this.tabButtons) {
        button.addEventListener('click', this.handleTabClick)
      }

      for (const button of this.addSingleButtons) {
        button.addEventListener('click', this.handleSingleAdd)
      }

      for (const card of this.styleCards) {
        card.addEventListener('click', this.handleStyleCardClick)
      }
      for (const view of this.allStyleViews) {
        view
          .querySelector('[data-clearance-v3-style-back]')
          ?.addEventListener('click', this.handleStyleBack)
        const results = view.querySelector('[data-clearance-v3-style-results]')
        results?.addEventListener('click', this.handleStyleSubNavClick)
        results?.addEventListener('click', this.handleScrollArrowClick)
      }
      this.packSection?.addEventListener('click', this.handleScrollArrowClick)
      this.setupCarouselScrollers()

      for (const button of this.modalSizeButtons) {
        button.addEventListener('click', this.handleModalSizeClick)
      }

      for (const button of this.modalWaistButtons) {
        button.addEventListener('click', this.handleModalWaistClick)
      }

      for (const button of this.modalLengthButtons) {
        button.addEventListener('click', this.handleModalLengthClick)
      }

      for (const button of this.modalTopLengthButtons) {
        button.addEventListener('click', this.handleModalTopLengthClick)
      }

      for (const button of this.modalCloseButtons) {
        button.addEventListener('click', this.handleCloseModal)
      }

      this.modalApplyButton?.addEventListener('click', this.handleModalApply)
      this.sizeModalOverlay?.addEventListener('click', this.handleCloseModal)
      this.showDealsButton?.addEventListener('click', this.handleShowDeals)
      this.changeSizeButton?.addEventListener('click', this.handleChangeSize)
      // A/B test concluded: automatic bundles Shop all listener disabled.
      this.viewCartButton?.addEventListener('click', this.handleViewCart)
      document.addEventListener('cartaddsuccess', this.handleCartRefresh)
      document.addEventListener('cartupdatesuccess', this.handleCartRefresh)
      document.addEventListener('clearanceatcsuccess', this.handleQuickAddSuccess)

      this.restoreSelectedSize()
      if (!this.dataset.clearanceCollection) this.setupSinglesPagination()
      this.setupSinglesDomPagination()
      this.updateTierStrip({ count: this.clearanceCount, subtotal: this.clearanceSubtotal })
      this.updateBrowse()
      // Deferred to here (rather than alongside renderPackCards above) because it renders
      // the singles grid, which needs this.singlesGrid / emptyElement resolved —
      // the cache-hit path populates the grid synchronously before its first await.
      this.fetchAndRenderSingles()
      this.refreshCartSummary()
      this.setupHeaderOffsetSync()
    }

    // Keeps the sticky sub-nav offset in sync with the actual browse-header height
    // so the pills pin directly below the clearance header.
    setupHeaderOffsetSync() {
      if (!this.browseHeader) return
      this.syncHeaderOffset()
      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(() => this.syncHeaderOffset())
        this.headerResizeObserver.observe(this.browseHeader)
      }
      window.addEventListener('resize', this.handleHeaderOffsetResize)
    }

    syncHeaderOffset() {
      // Subtract 1px to avoid a subpixel sliver where content shows above the sub-nav.
      const height = this.browseHeader?.offsetHeight || 0
      this.style.setProperty(
        '--clearance-v3-browse-header-height',
        `${Math.max(height - 1, 0)}px`
      )
    }

    getStickyElementHeight(selector, includeHidden = false) {
      const element = document.querySelector(selector)
      if (!element) return 0

      const styles = window.getComputedStyle(element)
      if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0

      const rect = element.getBoundingClientRect()
      if (includeHidden) return rect.height
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0

      return rect.height
    }

    getHeaderScrollOffset(targetTop) {
      const visibleHeaderHeight =
        this.getStickyElementHeight('.new-announcement-bar__wrapper.is-sticky') +
        this.getStickyElementHeight('.main-nav__wrapper.is-sticky')
      const isMobile = window.matchMedia('(max-width: 767px)').matches
      const willScrollUp = targetTop - visibleHeaderHeight < window.scrollY

      if (!isMobile || !willScrollUp) return visibleHeaderHeight

      return (
        this.getStickyElementHeight('.new-announcement-bar__wrapper.is-sticky', true) +
        this.getStickyElementHeight('.main-nav__wrapper.is-sticky', true)
      )
    }

    disconnectedCallback() {
      for (const button of this.sizeButtons || []) {
        button.removeEventListener('click', this.handleSizeClick)
      }

      for (const button of this.waistButtons || []) {
        button.removeEventListener('click', this.handleWaistClick)
      }

      for (const button of this.lengthButtons || []) {
        button.removeEventListener('click', this.handleLengthClick)
      }

      for (const button of this.topLengthButtons || []) {
        button.removeEventListener('click', this.handleTopLengthClick)
      }

      for (const button of this.bottomSizeButtons || []) {
        button.removeEventListener('click', this.handleBottomSizeClick)
      }

      for (const button of this.tabButtons || []) {
        button.removeEventListener('click', this.handleTabClick)
      }

      for (const button of this.addPackButtons || []) {
        button.removeEventListener('click', this.handlePackAdd)
      }

      for (const button of this.addSingleButtons || []) {
        button.removeEventListener('click', this.handleSingleAdd)
      }

      for (const button of this.modalSizeButtons || []) {
        button.removeEventListener('click', this.handleModalSizeClick)
      }

      for (const button of this.modalWaistButtons || []) {
        button.removeEventListener('click', this.handleModalWaistClick)
      }

      for (const button of this.modalLengthButtons || []) {
        button.removeEventListener('click', this.handleModalLengthClick)
      }

      for (const button of this.modalTopLengthButtons || []) {
        button.removeEventListener('click', this.handleModalTopLengthClick)
      }

      for (const button of this.modalCloseButtons || []) {
        button.removeEventListener('click', this.handleCloseModal)
      }

      this.modalApplyButton?.removeEventListener('click', this.handleModalApply)
      this.sizeModalOverlay?.removeEventListener('click', this.handleCloseModal)
      this.showDealsButton?.removeEventListener('click', this.handleShowDeals)
      this.changeSizeButton?.removeEventListener('click', this.handleChangeSize)
      // A/B test concluded: automatic bundles Shop all cleanup disabled.
      this.viewCartButton?.removeEventListener('click', this.handleViewCart)
      for (const card of this.styleCards || []) {
        card.removeEventListener('click', this.handleStyleCardClick)
      }
      for (const view of this.allStyleViews || []) {
        view
          .querySelector('[data-clearance-v3-style-back]')
          ?.removeEventListener('click', this.handleStyleBack)
        const results = view.querySelector('[data-clearance-v3-style-results]')
        results?.removeEventListener('click', this.handleStyleSubNavClick)
        results?.removeEventListener('click', this.handleScrollArrowClick)
      }
      this.packSection?.removeEventListener('click', this.handleScrollArrowClick)
      for (const scroller of this.carouselScrollers || []) {
        scroller.removeEventListener('scroll', this.handleCarouselScroll)
      }
      window.removeEventListener('resize', this.handleCarouselResize)
      window.removeEventListener('resize', this.handleHeaderOffsetResize)
      document.removeEventListener('cartaddsuccess', this.handleCartRefresh)
      document.removeEventListener('cartupdatesuccess', this.handleCartRefresh)
      document.removeEventListener('clearanceatcsuccess', this.handleQuickAddSuccess)
      if (this._modalEscHandler) {
        document.removeEventListener('keydown', this._modalEscHandler)
        this._modalEscHandler = null
      }
      this.singlesAbortController?.abort()
      this.singlesObserver?.disconnect()
      this.singlesDomObserver?.disconnect()
      this.headerResizeObserver?.disconnect()
    }

    handleSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopSize = size
      this.sizeCard?.classList.add('selected')

      for (const sizeButton of this.sizeButtons) {
        const isActive = sizeButton === button
        sizeButton.classList.toggle('active', isActive)
        sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      document.dispatchEvent(
        new CustomEvent('tc:top-size-change', {
          detail: { size },
        })
      )
      this.resetSinglesPage()
      this.updateBrowse()
    }

    handleWaistClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomWaist = size

      for (const btn of this.waistButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.updateBottomCardSelected()
      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.resetSinglesPage()
      this.updateBrowse()
    }

    handleLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomLength = size

      for (const btn of this.lengthButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.updateBottomCardSelected()
      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.resetSinglesPage()
      this.updateBrowse()
    }

    // Length is an optional refinement (unlike Top Size / Bottom Waist+Length), so it
    // doesn't factor into isAnySizeSelected()/setShowDealsReady — it just narrows what's
    // shown once the shopper has proceeded past step 1.
    handleTopLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopLength = size
      this.syncTopLengthButtons()
      this.saveSelectedSize()
      this.resetSinglesPage()
      this.updateBrowse()
    }

    // Reflects selectedTopLength onto both Tops Length rows — the step 1 size card and
    // the Change Sizes modal — so the two stay in lockstep however the value was set.
    syncTopLengthButtons() {
      const groups = [this.topLengthButtons, this.modalTopLengthButtons]
      for (const group of groups) {
        for (const btn of group || []) {
          const isActive =
            this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedTopLength)
          btn.classList.toggle('active', isActive)
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        }
      }
    }

    // Modal picks are held until Apply (like the modal's other rows); step 1 picks save now.
    handleBottomSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomSize = size
      this.syncBottomSizeButtons()
      if (button.hasAttribute('data-tc-modal-bottom-size-option')) return

      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.resetSinglesPage()
      this.updateBrowse()
    }

    syncBottomSizeButtons() {
      for (const btn of this.bottomSizeButtons || []) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomSize)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }
    }

    updateBottomCardSelected() {
      const isSelected = !!(this.selectedBottomWaist && this.selectedBottomLength)
      this.bottomCard?.classList.toggle('selected', isSelected)
    }

    isAnySizeSelected() {
      return !!(
        this.selectedTopSize ||
        this.selectedBottomSize ||
        (this.selectedBottomWaist && this.selectedBottomLength)
      )
    }

    showStepTwo(shouldScroll) {
      if (this.hero) this.hero.hidden = false
      if (this.stepOne) this.stepOne.hidden = true

      if (this.stepTwo) {
        this.stepTwo.hidden = false
      }

      this.updateBrowse()
      this.refreshCartSummary()
      this.maybeLoadVisibleSinglesPage()
      this.maybeRevealMoreSingles()

      this.dispatchEvent(
        new CustomEvent('clearance-v3:show-deals', {
          bubbles: true,
          detail: {
            topSize: this.selectedTopSize,
            packObjectsCount: Number(this.stepTwo?.dataset.packObjectsCount || 0),
          },
        })
      )

      if (shouldScroll) {
        window.scrollTo({ top: 0 })
        window.requestAnimationFrame(() => {
          const activeTab = this.tabButtons.find(
            (button) => button.getAttribute('aria-pressed') === 'true'
          )
          activeTab?.focus()
        })
      }
    }

    handleShowDeals() {
      if (!this.isAnySizeSelected()) return

      this.saveSelectedSize()
      this.showStepTwo(true)
    }

    _setActiveStyleTab(tab) {
      const view = (this.allStyleViews || []).find((v) => v.dataset.tab === tab)
      if (!view) return
      this.styleView = view
      this.styleTiles = view.querySelector('[data-clearance-v3-style-tiles]')
      this.styleResults = view.querySelector('[data-clearance-v3-style-results]')
      this.styleResultsTitle = view.querySelector('[data-clearance-v3-style-results-title]')
      this.styleBackButton = view.querySelector('[data-clearance-v3-style-back]')
      this.styleSkeleton = view.querySelector('[data-clearance-v3-style-skeleton]')
    }

    handleTabClick(event) {
      const button = event.currentTarget
      const tab = button.dataset.tab || 'deals'
      this.activeTab = tab

      for (const tabButton of this.tabButtons) {
        const isActive = tabButton === button
        tabButton.classList.toggle('active', isActive)
        tabButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const view of this.allStyleViews || []) view.hidden = true
      if (tab !== 'deals') this._setActiveStyleTab(tab)

      this.updateBrowse()
      if (tab === 'deals') {
        this.maybeLoadVisibleSinglesPage()
        this.maybeRevealMoreSingles()
      }
      if (tab !== 'deals') this.scrollProductsAreaIntoViewIfHeroHidden()
    }

    /* A/B test concluded: automatic bundles Shop all handler retained for reference.
    handleShopAllBundles() {
      const stylesTabButton = this.tabButtons.find((button) => button.dataset.tab === 'mens')
      if (!stylesTabButton) return

      stylesTabButton.click()
      stylesTabButton.focus()
    }
    */

    // When the hero has scrolled off screen, bring the products area to the top,
    // offset by the sticky header so its top isn't hidden underneath it.
    scrollProductsAreaIntoViewIfHeroHidden() {
      if (!this.productsArea || this.isElementInViewport(this.hero)) return
      const productsTop = this.productsArea.getBoundingClientRect().top + window.scrollY
      const stickyOffset =
        this.getHeaderScrollOffset(productsTop) + (this.browseHeader?.offsetHeight || 0)
      const top = productsTop - stickyOffset
      window.scrollTo({ top, behavior: this.getScrollBehavior() })
    }

    getScrollBehavior() {
      const prefersReducedMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      return prefersReducedMotion ? 'auto' : 'smooth'
    }

    isElementInViewport(element) {
      if (!element) return false
      const rect = element.getBoundingClientRect()
      return (
        rect.bottom > 0 &&
        rect.top < (window.innerHeight || document.documentElement.clientHeight)
      )
    }

    setupSinglesPagination() {
      if (!this.singlesGrid || !this.singlesSentinel) return

      this.updateSinglesPaginationState()
      if (!this.singlesNextPage) return

      if ('IntersectionObserver' in window) {
        this.singlesObserver?.disconnect()
        this.singlesObserver = new IntersectionObserver(this.handleSinglesIntersection, {
          rootMargin: '700px 0px',
        })
        this.singlesObserver.observe(this.singlesSentinel)
        this.maybeLoadVisibleSinglesPage()
        return
      }

      this.loadMoreSingles()
    }

    updateSinglesPaginationState() {
      const hasNextPage = Number(this.singlesNextPage || 0) > 0
      if (this.singlesSentinel) this.singlesSentinel.hidden = !hasNextPage
      if (!hasNextPage) this.singlesObserver?.disconnect()
    }

    // DOM pagination for the client-rendered Pick & Mix grid. Distinct from
    // setupSinglesPagination above, which fetches further *server* pages via the Section
    // Rendering API and only runs when there's no collection handle to query directly.
    setupSinglesDomPagination() {
      if (!this.singlesSentinel) return

      if (!('IntersectionObserver' in window)) {
        // No observer to drive reveals — show everything rather than trap cards behind a
        // page boundary the shopper can never cross.
        this.singlesShownCount = Number.MAX_SAFE_INTEGER
        return
      }

      this.singlesDomObserver?.disconnect()
      this.singlesDomObserver = new IntersectionObserver(this.handleSinglesDomIntersection, {
        rootMargin: '700px 0px',
      })
      this.singlesDomObserver.observe(this.singlesSentinel)
    }

    handleSinglesDomIntersection(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        this.revealMoreSingles()
        return
      }
    }

    // Mirrors maybeLoadVisibleSinglesPage, for DOM reveals instead of server pages. Call
    // whenever the grid (re)enters view — switching to Deals, or leaving step 1 — since the
    // sentinel may already sit in view without a fresh intersection to trigger it.
    maybeRevealMoreSingles() {
      if (!this.hasMoreSingles) return
      if (!this.isSinglesSentinelNearViewport()) return

      window.requestAnimationFrame(() => this.revealMoreSingles())
    }

    revealMoreSingles() {
      if (!this.hasMoreSingles) return
      if (this.activeTab !== 'deals' || this.stepTwo?.hidden) return

      this.singlesShownCount += this.singlesPageSize
      this.updateBrowse()
      // A freshly revealed page can be shorter than the viewport, leaving the sentinel
      // still in view without firing a new intersection — keep draining until it isn't.
      this.maybeRevealMoreSingles()
    }

    // Called whenever the size/length filter changes so a new result set starts at page 1
    // instead of inheriting however deep the shopper had scrolled the previous one.
    resetSinglesPage() {
      if (this.singlesShownCount === Number.MAX_SAFE_INTEGER) return
      this.singlesShownCount = this.singlesPageSize
    }

    handleSinglesIntersection(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue

        this.loadMoreSingles()
        return
      }
    }

    async loadMoreSingles() {
      if (this.isLoadingMoreSingles || !this.singlesNextPage || !this.singlesGrid) return
      if (this.activeTab !== 'deals' || this.stepTwo?.hidden) return

      this.isLoadingMoreSingles = true
      this.setSinglesLoading(true)
      this.singlesAbortController = new AbortController()
      let nextPage = this.singlesNextPage
      let appendedCount = 0

      try {
        while (nextPage && appendedCount === 0) {
          const pageData = await this.fetchSinglesPage(
            nextPage,
            this.singlesAbortController.signal
          )
          if (!pageData.grid) {
            nextPage = 0
            break
          }

          appendedCount += this.appendSinglesCards(pageData.grid)
          nextPage = pageData.nextPage
        }

        this.singlesNextPage = nextPage
        this.singlesGrid.dataset.nextPage = nextPage ? String(nextPage) : ''
        this.refreshSingleCardBindings()
        this.clearanceVariantIds = this.buildClearanceVariantIds()
        await this.refreshCartSummary()
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load more Clearance V3 products', error)
        }
      } finally {
        this.isLoadingMoreSingles = false
        this.setSinglesLoading(false)
        this.updateSinglesPaginationState()
        this.maybeLoadVisibleSinglesPage()
      }
    }

    async fetchSinglesPage(page, signal) {
      const response = await fetch(this.getSinglesPageUrl(page), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      if (!response.ok) throw new Error(`Unable to fetch Clearance V3 page ${page}`)

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const grid = doc.querySelector('[data-clearance-v3-singles-grid]')
      const nextPage = Number(grid?.dataset.nextPage || 0)

      return { grid, nextPage }
    }

    appendSinglesCards(nextGrid) {
      const existingProductIds = new Set()
      const currentCards = Array.from(
        this.singlesGrid.querySelectorAll('[data-clearance-v3-single-card]')
      )
      for (const card of currentCards) {
        const productId = this.getSingleCardProductId(card)
        if (productId) existingProductIds.add(productId)
      }

      let appendedCount = 0
      const nextCards = Array.from(nextGrid.querySelectorAll('[data-clearance-v3-single-card]'))
      for (const card of nextCards) {
        const productId = this.getSingleCardProductId(card)
        if (productId && existingProductIds.has(productId)) continue

        const importedCard = document.importNode(card, true)
        this.stripLengthIfNotEligibleForLengthFilter(importedCard)
        this.singlesGrid.appendChild(importedCard)
        if (productId) existingProductIds.add(productId)
        appendedCount += 1
      }

      return appendedCount
    }

    getSingleCardProductId(card) {
      const productId = String(card?.dataset.productId || '').trim()
      if (productId) return productId

      const script = card?.querySelector('[data-clearance-v3-single-products]')
      if (!script) return ''

      try {
        const products = JSON.parse(script.textContent || '[]')
        return products[0]?.productId ? String(products[0].productId) : ''
      } catch (error) {
        return ''
      }
    }

    getSinglesPageUrl(page) {
      const url = new URL(window.location.href)
      const sectionId = this.dataset.sectionId || ''
      if (sectionId) url.searchParams.set('section_id', sectionId)
      url.searchParams.set('page', String(page))
      return url.toString()
    }

    setSinglesLoading(isLoading) {
      if (this.singlesLoading) this.singlesLoading.hidden = !isLoading
      if (this.singlesGrid) {
        if (isLoading) {
          this.singlesGrid.setAttribute('aria-busy', 'true')
        } else {
          this.singlesGrid.removeAttribute('aria-busy')
        }
      }
    }

    maybeLoadVisibleSinglesPage() {
      if (!this.singlesNextPage || this.isLoadingMoreSingles) return
      if (!this.isSinglesSentinelNearViewport()) return

      window.requestAnimationFrame(() => this.loadMoreSingles())
    }

    isSinglesSentinelNearViewport() {
      if (!this.singlesSentinel || this.singlesSentinel.hidden) return false
      if (this.activeTab !== 'deals' || this.stepTwo?.hidden) return false

      const rect = this.singlesSentinel.getBoundingClientRect()
      return rect.top <= window.innerHeight + 700
    }

    handleStyleCardClick(event) {
      const card = event.currentTarget
      const handle = card.dataset.categoryHandle || ''
      const title = card.querySelector('.style-card__title')?.textContent?.trim() || ''
      this.activeStyleTrigger =
        event.target.closest('.style-card__trigger') || document.activeElement

      if (this.isBottomsCategory(handle) && !this.selectedBottomWaist && !this.selectedBottomSize) {
        this._pendingStyleHandle = handle
        this._pendingStyleTitle = title
        this.openSizeModal(true)
        return
      }

      if (this.styleTiles) this.styleTiles.hidden = true
      if (this.styleResults) this.styleResults.hidden = false
      if (this.styleResultsTitle) this.styleResultsTitle.textContent = title

      for (const result of this.styleResults?.querySelectorAll('.category-result') || []) {
        result.hidden = result.dataset.categoryHandle !== handle
      }

      if (this.isFetchingSingles && this.styleSkeleton) {
        this.styleSkeleton.hidden = false
        if (this.styleResults) this.styleResults.setAttribute('aria-busy', 'true')
      }

      this.populateCategoryResult(handle)
      this.renderActiveStyleBundles()
      this.updateBrowse()
      window.requestAnimationFrame(() => this.styleBackButton?.focus())
    }

    handleStyleBack() {
      if (this.styleTiles) this.styleTiles.hidden = false
      if (this.styleResults) this.styleResults.hidden = true
      this.updateBrowse()
      window.requestAnimationFrame(() => this.activeStyleTrigger?.focus())
    }

    handleStyleSubNavClick(event) {
      const chip = event.target.closest('[data-clearance-v3-style-sub-nav-button]')
      if (!chip) return

      const nav = chip.closest('[data-clearance-v3-style-sub-nav]')
      for (const c of nav?.querySelectorAll('[data-clearance-v3-style-sub-nav-button]') || []) {
        const isActive = c === chip
        c.classList.toggle('active', isActive)
        c.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      const row = chip.dataset.target ? document.getElementById(chip.dataset.target) : null
      if (row) {
        const browseHeader = this.querySelector('.browse-header')
        const subNavEl = chip.closest('[data-clearance-v3-style-sub-nav]')
        const rowTop = row.getBoundingClientRect().top + window.scrollY
        const offset =
          this.getHeaderScrollOffset(rowTop) +
          (browseHeader?.getBoundingClientRect().height || 0) +
          (subNavEl?.getBoundingClientRect().height || 0)
        window.scrollTo({
          top: rowTop - offset,
          behavior: this.getScrollBehavior(),
        })
      }
    }

    setupCarouselScrollers() {
      this.carouselScrollers = Array.from(this.querySelectorAll(SCROLL_TRACK_SELECTOR))
      for (const scroller of this.carouselScrollers) {
        scroller.addEventListener('scroll', this.handleCarouselScroll, { passive: true })
      }
      window.addEventListener('resize', this.handleCarouselResize)
    }

    handleCarouselScroll(event) {
      this.updateScrollArrows(event.currentTarget)
    }

    handleCarouselResize() {
      this.refreshScrollArrows()
    }

    handleScrollArrowClick(event) {
      const arrow = event.target.closest(
        '[data-clearance-v3-scroll-prev], [data-clearance-v3-scroll-next]'
      )
      if (!arrow || !this.contains(arrow)) return

      const wrap = arrow.closest(SCROLL_TRACK_WRAP_SELECTOR)
      const scroller = wrap?.querySelector(SCROLL_TRACK_SELECTOR)
      if (!scroller) return

      const isPrev = arrow.hasAttribute('data-clearance-v3-scroll-prev')
      const amount = scroller.clientWidth * 0.8
      scroller.scrollBy({
        left: isPrev ? -amount : amount,
        behavior: this.getScrollBehavior(),
      })
    }

    // Toggle arrow visibility based on the scroller's current scroll position.
    updateScrollArrows(scroller) {
      const wrap = scroller?.closest(SCROLL_TRACK_WRAP_SELECTOR)
      if (!wrap) return

      const prev = wrap.querySelector('[data-clearance-v3-scroll-prev]')
      const next = wrap.querySelector('[data-clearance-v3-scroll-next]')
      const maxScroll = scroller.scrollWidth - scroller.clientWidth
      const canScrollPrev = scroller.scrollLeft > 1
      const canScrollNext = scroller.scrollLeft < maxScroll - 1

      if (prev) {
        prev.classList.toggle('is-visible', canScrollPrev)
        prev.disabled = !canScrollPrev
        prev.setAttribute('aria-hidden', canScrollPrev ? 'false' : 'true')
      }
      if (next) {
        next.classList.toggle('is-visible', canScrollNext)
        next.disabled = !canScrollNext
        next.setAttribute('aria-hidden', canScrollNext ? 'false' : 'true')
      }
    }

    // Defaults to every track in the section — the Quick Bundles carousel included — so a
    // resize re-measures both kinds; callers pass a scope when only one row moved.
    refreshScrollArrows(scope) {
      const root = scope || this
      for (const scroller of root.querySelectorAll(SCROLL_TRACK_SELECTOR)) {
        this.updateScrollArrows(scroller)
      }
    }

    updateStyleRowCounts() {
      if (!this.styleResults || this.styleResults.hidden) return
      const activeResult = this.styleResults.querySelector('.category-result:not([hidden])')
      if (!activeResult) return

      for (const subRow of activeResult.querySelectorAll('[data-clearance-v3-style-row]')) {
        const singleCount = subRow.querySelectorAll(
          '[data-clearance-v3-single-card]:not([hidden])[data-size-available="true"]'
        ).length
        const hasBundle = !!subRow.querySelector(
          '[data-clearance-v3-style-bundle-slot]:not([hidden]) [data-clearance-v3-style-bundle-card]:not([hidden])'
        )
        const count = singleCount + (hasBundle ? 1 : 0)
        // A bundle alone isn't enough to keep the row up — it only stays visible when
        // .sub-items-wrap actually has an available single item.
        subRow.hidden = singleCount === 0

        const cntEl = subRow.querySelector('.sub-cnt')
        if (cntEl) cntEl.textContent = `${count} ${count === 1 ? 'deal' : 'deals'}`

        const chip = activeResult.querySelector(
          `[data-clearance-v3-style-sub-nav-button][data-target="${subRow.id}"]`
        )
        if (chip) {
          const countEl = chip.querySelector('.sub-chip__count')
          if (countEl) countEl.textContent = count
          if (!this.isFetchingSingles)
            chip.dataset.sizeAvailable = singleCount > 0 ? 'true' : 'false'
        }
      }

      this.refreshScrollArrows(activeResult)
    }

    populateCategoryResult(handle, singles = null) {
      const result = this.styleResults?.querySelector(`[data-category-handle="${handle}"]`)
      if (!result) return

      const allSingles = singles || this.parseAllProducts()?.singles || []
      this.syncStyleFallbackRows(allSingles)

      for (const subRow of result.querySelectorAll('[data-clearance-v3-style-row]')) {
        const pdpAppId = subRow.dataset.pdpAppId
        const itemsContainer = subRow.querySelector('[data-clearance-v3-style-row-items]')
        if (!itemsContainer || !pdpAppId) continue

        const existingProductIds = new Set()
        for (const card of itemsContainer.querySelectorAll('[data-clearance-v3-single-card]')) {
          const productId = this.getSingleCardProductId(card)
          if (productId) existingProductIds.add(productId)
        }
        const products = allSingles.filter((p) => this.isProductInStyleRow(p, subRow))
        const fragment = document.createDocumentFragment()
        for (const product of products) {
          const productId = product.productId ? String(product.productId) : ''
          if (productId && existingProductIds.has(productId)) continue
          const card = this.createSingleCardElement(product, 'style')
          fragment.appendChild(card)
          if (productId) existingProductIds.add(productId)
        }
        itemsContainer.appendChild(fragment)
      }

      this.renderStyleBundlesForResult(result, allSingles)
      this.refreshSingleCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
    }

    // ---- Choose style fallbacks ----------------------------------------------------------
    // Full write-up, settings and rollback: documentation/clearance-v3-style-fallbacks.md.
    //
    // A style row normally takes only the clearance products whose PDP app id equals its
    // data-pdp-app-id. Products whose PDP app no menu row points at are therefore only
    // reachable from Top Deals. Each enabled layer gives such a product a home, first
    // match wins:
    //   1. family    — an existing row whose PDP app shares the product's family_name
    //                  (recorded on the row as data-fallback-pdp-app-ids)
    //   2. category  — a generated row in the style tile its PDP app's menu_category names
    //   3. catchAll  — a generated row in the catch-all tile (Accessories by default)
    // Generated rows carry the product's real PDP app id as data-pdp-app-id, so counts,
    // size filtering, arrows and in-row bundles treat them exactly like menu rows. They are
    // marked data-clearance-v3-fallback-row and kept out of the Top Deals Quick Bundles.
    // Every placement is also recorded for the theme-editor report.

    readStyleFallbackSettings() {
      return {
        family: this.dataset.styleFallbackFamily === 'true',
        category: this.dataset.styleFallbackCategory === 'true',
        // Grid category handle of the catch-all tile; blank switches the layer off.
        catchAll: String(this.dataset.styleFallbackCatchAll || '').trim(),
      }
    }

    isAnyStyleFallbackEnabled() {
      const { family, category, catchAll } = this.styleFallbacks || {}
      return !!(family || category || catchAll)
    }

    // Clones the first server-rendered row and its chip as the blueprint for generated rows,
    // the same way packPlaceholderTemplate is taken from the server markup, so the two can't
    // drift apart.
    cloneStyleFallbackRowTemplate() {
      const row = this.querySelector('[data-clearance-v3-style-row]')
      const chip = row
        ? this.querySelector(`[data-clearance-v3-style-sub-nav-button][data-target="${row.id}"]`)
        : null
      if (!row || !chip) return null
      return { row: row.cloneNode(true), chip: chip.cloneNode(true) }
    }

    getStyleRowFallbackIds(subRow) {
      return String(subRow?.dataset.fallbackPdpAppIds || '')
        .split(',')
        .filter(Boolean)
    }

    // Row membership for display: the row's own PDP app plus any layer-1 family matches.
    // Bundle builders deliberately keep matching on data-pdp-app-id alone, so a bundle never
    // mixes two versions of a style (e.g. Active Polo 1.0 and 2.0).
    isProductInStyleRow(product, subRow) {
      const pdpAppId = String(product?.pdpAppId || '')
      if (!pdpAppId) return false
      if (pdpAppId === String(subRow?.dataset.pdpAppId || '')) return true
      return this.getStyleRowFallbackIds(subRow).includes(pdpAppId)
    }

    normalizeMatchName(name) {
      return String(name || '')
        .trim()
        .toLowerCase()
    }

    // Idempotent, and cheap to call repeatedly: it runs on every partial fetch page and every
    // updateBrowse pass, so it bails unless the singles list has changed since the last run.
    syncStyleFallbackRows(singles) {
      if (!this.styleResults || !this.isAnyStyleFallbackEnabled() || !singles?.length) return

      const signature = `${singles.length}:${singles[singles.length - 1]?.productId || ''}`
      if (this.styleFallbackSignature === signature) return
      this.styleFallbackSignature = signature

      const menuRows = Array.from(
        this.styleResults.querySelectorAll(
          '[data-clearance-v3-style-row]:not([data-clearance-v3-fallback-row])'
        )
      )
      const menuPdpAppIds = new Set(
        menuRows.map((row) => String(row.dataset.pdpAppId || '')).filter(Boolean)
      )
      const menuRowsByFamily = this.groupStyleRowsByFamily(menuRows)
      // Rebuilt on every run, so it always reflects the full current singles list.
      this.styleFallbackPlacements = []

      for (const product of singles) {
        const pdpAppId = String(product.pdpAppId || '')
        if (pdpAppId && menuPdpAppIds.has(pdpAppId)) continue
        // No PDP app means nothing to key a row on; such products stay Top Deals only.
        if (!pdpAppId) {
          this.recordStyleFallbackPlacement('unplaced', product, [])
          continue
        }

        if (this.styleFallbacks.family) {
          const familyRows = this.addProductToFamilyRows(product, menuRowsByFamily)
          if (familyRows) {
            this.recordStyleFallbackPlacement('family', product, familyRows)
            continue
          }
        }

        const target = this.getStyleFallbackCategoryResult(product)
        const row = target ? this.ensureStyleFallbackRow(target.categoryResult, product) : null
        this.recordStyleFallbackPlacement(row ? target.layer : 'unplaced', product, row ? [row] : [])
      }
    }

    recordStyleFallbackPlacement(layer, product, rows) {
      this.styleFallbackPlacements.push({
        layer,
        product,
        locations: rows.map((row) => ({
          tile: this.getStyleTileTitle(row.closest('.category-result')?.dataset.categoryHandle),
          row: row.querySelector('[data-clearance-v3-style-row-title]')?.textContent.trim() || '',
        })),
      })
    }

    getStyleTileTitle(categoryHandle) {
      const card = (this.styleCards || []).find(
        (styleCard) => styleCard.dataset.categoryHandle === categoryHandle
      )
      return card?.querySelector('.style-card__title')?.textContent.trim() || categoryHandle || ''
    }

    groupStyleRowsByFamily(rows) {
      const rowsByFamily = new Map()
      for (const row of rows) {
        const family = this.normalizeMatchName(row.dataset.familyName)
        if (!family) continue
        if (!rowsByFamily.has(family)) rowsByFamily.set(family, [])
        rowsByFamily.get(family).push(row)
      }
      return rowsByFamily
    }

    // Layer 1. Returns the family rows the product joined, or null when there are none.
    addProductToFamilyRows(product, menuRowsByFamily) {
      const rows = menuRowsByFamily.get(this.normalizeMatchName(product.familyName))
      if (!rows) return null

      const pdpAppId = String(product.pdpAppId)
      for (const row of rows) {
        const ids = this.getStyleRowFallbackIds(row)
        if (ids.includes(pdpAppId)) continue
        ids.push(pdpAppId)
        row.dataset.fallbackPdpAppIds = ids.join(',')
      }
      return rows
    }

    // Layers 2 and 3: the product's menu category when that tile is on the page, else the
    // catch-all tile. Null when neither layer is enabled or its tile isn't on the page.
    getStyleFallbackCategoryResult(product) {
      if (this.styleFallbacks.category) {
        const categoryResult = this.findStyleCategoryResultForProduct(product)
        if (categoryResult) return { categoryResult, layer: 'category' }
      }
      if (!this.styleFallbacks.catchAll) return null
      const categoryResult = this.styleResults.querySelector(
        `.category-result[data-category-handle="${CSS.escape(this.styleFallbacks.catchAll)}"]`
      )
      return categoryResult ? { categoryResult, layer: 'catchAll' } : null
    }

    // Layer 2 lookup: by grid category id first, then by category name. The name match covers
    // categories that exist twice in admin — e.g. PDP apps point at "Dress Shirts"
    // (dress-shirts-1, used by the main mobile nav) while the clearance menu holds the older
    // "Dress Shirts" (dress-shirts), so the ids never meet.
    findStyleCategoryResultForProduct(product) {
      const categoryResults = Array.from(this.styleResults.querySelectorAll('.category-result'))
      const byId = categoryResults.find(
        (result) => product.menuCategoryId && result.dataset.categoryId === product.menuCategoryId
      )
      if (byId) return byId

      const categoryName = this.normalizeMatchName(product.menuCategoryName)
      if (!categoryName) return null
      return (
        categoryResults.find(
          (result) => this.normalizeMatchName(result.dataset.categoryName) === categoryName
        ) || null
      )
    }

    // One generated row per PDP app per category; later products of the same PDP app land in
    // it through the normal data-pdp-app-id match in populateCategoryResult. Returns the row
    // (new or existing), or null when there's no template to build one from.
    ensureStyleFallbackRow(categoryResult, product) {
      const template = this.styleFallbackRowTemplate
      const subNav = categoryResult.querySelector('[data-clearance-v3-style-sub-nav]')
      const pdpAppId = String(product.pdpAppId)
      if (!template || !subNav) return null
      const existingRow = categoryResult.querySelector(
        `[data-clearance-v3-style-row][data-pdp-app-id="${pdpAppId}"]`
      )
      if (existingRow) return existingRow

      const idSuffix = `fallback-${categoryResult.dataset.categoryHandle || ''}-${pdpAppId}`
      const title = product.familyTitle || product.title || ''
      const row = this.buildStyleFallbackRow(template.row, idSuffix, title, product)
      const chip = this.buildStyleFallbackChip(template.chip, row.id, title)

      // A tile whose menu items are all blank has no server-rendered chips, so its first
      // generated one takes the active state the snippet gives every other first chip.
      const isFirstChip = !subNav.querySelector('[data-clearance-v3-style-sub-nav-button]')
      chip.classList.toggle('active', isFirstChip)
      chip.setAttribute('aria-pressed', isFirstChip ? 'true' : 'false')

      subNav.appendChild(chip)
      categoryResult.appendChild(row)
      return row
    }

    buildStyleFallbackRow(templateRow, idSuffix, title, product) {
      const row = templateRow.cloneNode(true)
      const rowId = `c3-sub-${idSuffix}`
      const labelId = `c3-label-${idSuffix}`
      const trackId = `c3-track-${idSuffix}`

      row.id = rowId
      row.dataset.pdpAppId = String(product.pdpAppId)
      row.dataset.productHandle = product.handle || ''
      row.dataset.itemHandle = ''
      row.dataset.familyName = product.familyName || ''
      row.dataset.clearanceV3FallbackRow = ''
      delete row.dataset.fallbackPdpAppIds

      const label = row.querySelector('[data-clearance-v3-style-row-title]')
      if (label) {
        label.id = labelId
        label.textContent = title
      }
      const count = row.querySelector('.sub-cnt')
      if (count) count.textContent = '0 deals'

      const track = row.querySelector('[data-clearance-v3-style-row-scroller]')
      if (track) {
        track.id = trackId
        track.setAttribute('aria-labelledby', labelId)
      }
      for (const arrow of row.querySelectorAll(
        '[data-clearance-v3-scroll-prev], [data-clearance-v3-scroll-next]'
      )) {
        arrow.setAttribute('aria-controls', trackId)
      }

      row.querySelector('[data-clearance-v3-style-row-items]')?.replaceChildren()
      const bundleSlot = row.querySelector('[data-clearance-v3-style-bundle-slot]')
      if (bundleSlot) {
        bundleSlot.replaceChildren()
        bundleSlot.hidden = true
      }

      // Rows created after setupCarouselScrollers ran need their own scroll listener.
      track?.addEventListener('scroll', this.handleCarouselScroll, { passive: true })
      return row
    }

    buildStyleFallbackChip(templateChip, rowId, title) {
      const chip = templateChip.cloneNode(true)
      const countTemplate = chip.querySelector('.sub-chip__count')

      chip.setAttribute('aria-controls', rowId)
      chip.dataset.target = rowId
      chip.dataset.sizeAvailable = 'true'
      chip.textContent = title
      if (countTemplate) {
        countTemplate.innerHTML = '&nbsp;'
        chip.appendChild(countTemplate)
      }
      return chip
    }

    // Theme editor only (the container is rendered under request.design_mode). Lists every
    // product a fallback placed, grouped by layer, plus any the fallbacks couldn't place.
    // Covers all fetched singles regardless of the shopper's size selection.
    renderStyleFallbackReport() {
      const report = this.querySelector('[data-clearance-v3-fallback-report]')
      if (!report) return

      const placements = this.styleFallbackPlacements || []
      const summary = report.querySelector('[data-clearance-v3-fallback-report-summary]')
      const body = report.querySelector('[data-clearance-v3-fallback-report-body]')
      if (!summary || !body) return

      const signature = `${this.styleFallbackSignature || ''}:${placements.length}`
      if (report.dataset.signature === signature) return
      report.dataset.signature = signature

      if (!this.isAnyStyleFallbackEnabled()) {
        summary.textContent = 'Choose style fallbacks: all layers are off'
        body.replaceChildren()
        return
      }

      const placedCount = placements.filter((placement) => placement.layer !== 'unplaced').length
      summary.textContent = `Choose style fallbacks: ${placedCount} products placed, ${
        placements.length - placedCount
      } unplaced (theme editor only)`

      const layers = [
        ['family', 'Layer 1: matched by family name (joined an existing row)'],
        ['category', "Layer 2: generated row in the PDP app's menu category"],
        ['catchAll', `Layer 3: generated row in the catch-all tile (${this.styleFallbacks.catchAll})`],
        ['unplaced', 'Not placed: no PDP app, or its layer is off or has no tile on this page'],
      ]
      const fragment = document.createDocumentFragment()
      for (const [layer, caption] of layers) {
        const rows = placements.filter((placement) => placement.layer === layer)
        if (rows.length) fragment.appendChild(this.buildStyleFallbackReportTable(caption, rows))
      }
      body.replaceChildren(fragment)
    }

    buildStyleFallbackReportTable(caption, placements) {
      const table = document.createElement('table')
      table.className = 'fallback-report__table'

      const captionEl = table.createCaption()
      captionEl.textContent = `${caption} (${placements.length})`

      const headRow = table.createTHead().insertRow()
      for (const heading of ['Product', 'Handle', 'PDP app', 'Family name', 'Shown in']) {
        const th = document.createElement('th')
        th.scope = 'col'
        th.textContent = heading
        headRow.appendChild(th)
      }

      const tbody = table.createTBody()
      for (const { product, locations } of placements) {
        const cells = [
          product.title,
          product.handle,
          product.familyHandle || '(none)',
          product.familyName || '(none)',
          locations.map(({ tile, row }) => `${tile} → ${row}`).join('; ') || '—',
        ]
        const tr = tbody.insertRow()
        for (const value of cells) tr.insertCell().textContent = value
      }
      return table
    }

    handleChangeSize() {
      // The "Update Sizes" editor lets shoppers adjust both tops and bottoms.
      this.openSizeModal(true)
    }

    async handleViewCart(event) {
      event.preventDefault()

      await this.openCartDrawer()
    }

    async openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        if (typeof cartDrawer.open !== 'function' && window.customElements?.whenDefined) {
          try {
            await Promise.race([
              window.customElements.whenDefined('cart-drawer'),
              new Promise((resolve) => window.setTimeout(resolve, 500)),
            ])
          } catch (error) {
            console.warn('Unable to wait for cart drawer definition', error)
          }
        }

        if (typeof cartDrawer.open === 'function') {
          try {
            cartDrawer.open(this.viewCartButton)
            return
          } catch (error) {
            console.warn('Unable to open cart drawer', error)
          }
        }
      }

      const cartTrigger = document.querySelector('#cart-icon-bubble, #mini-cart-open')
      if (cartTrigger) {
        cartTrigger.click()
        return
      }

      window.location.href = `${this.routesRoot()}cart`
    }

    isBottomsCategory(handle) {
      const h = (handle || '').toLowerCase()
      return ['bottom', 'pant', 'denim', 'jean', 'chino'].some((kw) => h.includes(kw))
    }

    openSizeModal(showBottoms = false) {
      if (this.modalBottomsSection) this.modalBottomsSection.hidden = !showBottoms

      const allSingles = this.parseAllProducts()?.singles || []

      for (const button of this.modalSizeButtons) {
        const size = button.dataset.size || ''
        if (allSingles.length > 0) {
          const hasStock = allSingles.some((product) =>
            this.doesProductHaveTopSizeStock(product, size)
          )
          button.hidden = !hasStock
        }
        const isActive = this.normalizeSize(size) === this.normalizeSize(this.selectedTopSize)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }
      this.syncBottomSizeButtons()

      for (const button of this.modalWaistButtons) {
        const isActive =
          this.normalizeSize(button.dataset.size) === this.normalizeSize(this.selectedBottomWaist)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const button of this.modalLengthButtons) {
        const isActive =
          this.normalizeSize(button.dataset.size) ===
          this.normalizeSize(this.selectedBottomLength)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const button of this.modalTopLengthButtons) {
        const size = button.dataset.size || ''
        if (allSingles.length > 0) {
          const hasStock = allSingles.some(
            (product) =>
              product.available &&
              window.TcSizePreferences.getSizeCategoryForProductType(product.productType) ===
                'top' &&
              product.hasAlternateLength &&
              this.normalizeSize(product.length) === this.normalizeSize(size)
          )
          button.hidden = !hasStock
        }
      }
      this.syncTopLengthButtons()

      this.sizeModal?.classList.add('open')
      this.sizeModalOverlay?.classList.add('open')

      // a11y: expose the dialog, hide the page behind it, move focus in
      if (this.sizeModal) {
        this._modalReturnFocusEl = document.activeElement
        this.sizeModal.inert = false
        this.sizeModal.removeAttribute('aria-hidden')
        this.setPageInertForModal(true)
        this._modalEscHandler = (evt) => this.handleModalKeydown(evt)
        document.addEventListener('keydown', this._modalEscHandler)
        this.getModalFocusableElements()[0]?.focus()
      }
    }

    closeSizeModal(restoreFocus = true) {
      const shouldRestoreFocus = restoreFocus !== false

      // a11y: restore the page before moving focus out of the dialog.
      if (this.sizeModal) {
        this.setPageInertForModal(false)
        if (this._modalEscHandler) {
          document.removeEventListener('keydown', this._modalEscHandler)
          this._modalEscHandler = null
        }
        if (
          shouldRestoreFocus &&
          this._modalReturnFocusEl?.isConnected &&
          this._modalReturnFocusEl?.focus
        ) {
          this._modalReturnFocusEl.focus()
        }
        this.sizeModal.inert = true
        this.sizeModal.setAttribute('aria-hidden', 'true')
        if (shouldRestoreFocus) {
          this._pendingStyleHandle = null
          this._pendingStyleTitle = null
        }
        this._modalReturnFocusEl = null
      }

      this.sizeModal?.classList.remove('open')
      this.sizeModalOverlay?.classList.remove('open')
    }

    // Inert everything at the modal's level except the modal + overlay while open
    setPageInertForModal(isOpen) {
      const siblings = this.sizeModal?.parentElement?.children || []
      for (const el of siblings) {
        if (el === this.sizeModal || el === this.sizeModalOverlay) continue
        el.inert = isOpen
      }
    }

    getModalFocusableElements() {
      if (!this.sizeModal) return []

      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')

      return Array.from(this.sizeModal.querySelectorAll(selector)).filter(
        (element) => !element.hidden && element.getClientRects().length > 0
      )
    }

    handleModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeSizeModal()
        return
      }

      if (event.key !== 'Tab') return

      const focusableElements = this.getModalFocusableElements()
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      if (!firstElement || !lastElement) {
        event.preventDefault()
        this.sizeModal?.focus()
        return
      }

      if (!this.sizeModal?.contains(document.activeElement)) {
        event.preventDefault()
        firstElement.focus()
        return
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    // Numeric-sizing cards (bottoms) show "<waist> / <length>"; letter-sized bottoms show
    // the Bottoms size; others show the top size.
    getPreferredSizeLabel(card) {
      const product = this.parseProducts(card, '[data-clearance-v3-single-products]')[0]
      if (product && this.getBottomSizeOptionIndex(product) >= 0) return this.selectedBottomSize
      if (card.dataset.isNumericSizing !== 'true') return this.selectedTopSize
      return [this.selectedBottomWaist, this.selectedBottomLength].filter(Boolean).join(' / ')
    }

    // Hides the "·" separator before the size when there's no size to show.
    renderCardSizeLabel(sizeElement, label) {
      sizeElement.textContent = label
      const separator = sizeElement.previousElementSibling
      if (separator) separator.hidden = !label
    }

    // The variant getVariantForSelectedSize resolves isn't always in the shopper's preferred
    // size — a single-variant product matches unconditionally, and a single-option bottom
    // matches on waist alone — so the card labels the variant that would actually be added
    // rather than the selection. With no resolvable variant (the "Unavailable in <size>"
    // case) there's nothing to name but the size the shopper asked for.
    getCardSizeLabel(card) {
      const product = this.parseProducts(card, '[data-clearance-v3-single-products]')[0]
      const variant = product ? this.getVariantForSelectedSize(product) : null
      return (
        (variant && this.getVariantSizeLabel(variant, product)) ||
        this.getPreferredSizeLabel(card)
      )
    }

    // Option values joined the same way the preferred bottoms label is
    // ("<waist> / <length>"); tops carry a single size option so they come out as the bare
    // size. Shopify's "Default Title" placeholder and any color option are dropped so only
    // sizing is named. Falls back to the variant title, then to '' so the caller can drop
    // back to the preferred label.
    getVariantSizeLabel(variant, product) {
      const color = this.normalizeSize(product?.color || '')
      const options = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .filter((option) => {
          const normalized = this.normalizeSize(option)
          return normalized !== 'DEFAULTTITLE' && (!color || normalized !== color)
        })
      if (options.length) return options.join(' / ')

      const title = variant.title || ''
      return this.normalizeSize(title) === 'DEFAULTTITLE' ? '' : title
    }

    // The SSR card snippet always renders a computed data-length regardless of product
    // type or family. Strip it unless the product is both (a) classified 'top' by
    // TcSizePreferences and (b) belongs to a family that actually has a sibling
    // Short/Regular/Tall length (data-has-alternate-length, mirroring the
    // alternative_length_family check in snippets/your-cut-selector.liquid) — a top
    // with no alternate-length sibling has no real Length choice to filter by.
    stripLengthIfNotEligibleForLengthFilter(card) {
      const isTop =
        window.TcSizePreferences.getSizeCategoryForProductType(card.dataset.productType) === 'top'
      const hasAlternateLength = card.dataset.hasAlternateLength === 'true'
      if (card.dataset.length && (!isTop || !hasAlternateLength)) {
        delete card.dataset.length
      }
    }

    // Nothing picked means Regular: the Tops Length row starts with no button
    // preselected, but filtering still behaves as though Regular were chosen.
    getEffectiveTopLength() {
      return this.selectedTopLength || 'Regular'
    }

    // The Short/Regular/Tall length filter only applies to tops that actually have a
    // sibling length family (see stripLengthIfNotEligibleForLengthFilter, which already
    // strips data-length from anything ineligible — either not classified 'top' by
    // TcSizePreferences, or a top with no alternate-length sibling to filter between).
    // The empty-cardLength check below is what makes both cases fall through here.
    // effectiveLength lets a caller filter against a resolved length (see
    // resolveSingleLengthFilter) instead of the shopper's raw selection.
    matchesTopLengthFilter(card, effectiveLength) {
      const cardLength = card.dataset.length || ''
      if (!cardLength) return true
      const target = effectiveLength || this.getEffectiveTopLength()
      return this.normalizeSize(cardLength) === this.normalizeSize(target)
    }

    // Pick & Mix must never bottom out at zero cards: if nothing in the shopper's Tops
    // Length is orderable in their size, fall back to Regular. The grid is DOM-paginated,
    // so a strict filter is only provisional while pages remain — an empty-looking grid
    // keeps the sentinel in view, which drains the remaining pages, and every appended
    // page re-runs updateBrowse. Deciding early would swap Regular cards out from under
    // the shopper the moment a matching page landed.
    resolveSingleLengthFilter(singleStates) {
      const selected = this.normalizeSize(this.getEffectiveTopLength())
      const regular = this.normalizeSize('Regular')
      if (selected === regular) return regular
      if (Number(this.singlesNextPage || 0) > 0) return selected

      const hasMatch = singleStates.some(
        (state) =>
          !state.isStyleCard &&
          state.hasProducts &&
          state.isAvailable &&
          this.matchesTopLengthFilter(state.card, selected)
      )

      return hasMatch ? selected : regular
    }

    // Deals packs carry a data-pack-length (from the pack metaobject's length, defaulting
    // to Regular); style bundles are assembled client-side and have none, so an empty
    // value falls through unfiltered — same convention as matchesTopLengthFilter.
    matchesPackLengthFilter(card, effectivePackLength) {
      const packLength = card.dataset.packLength || ''
      if (!packLength) return true
      return this.normalizeSize(packLength) === effectivePackLength
    }

    // Resolves which pack length the row should actually show. A shopper on Tall whose
    // Tall packs are all sold out (or who has no Tall packs at all) would otherwise see
    // an empty Deals row, so fall back to Regular whenever nothing matches. Takes the
    // pre-computed pack states so availability isn't parsed twice per card.
    resolvePackLengthFilter(packStates) {
      const selected = this.normalizeSize(this.getEffectiveTopLength())
      const regular = this.normalizeSize('Regular')
      if (selected === regular) return regular

      const hasMatch = packStates.some(
        (state) =>
          state.card.dataset.packLength &&
          this.normalizeSize(state.card.dataset.packLength) === selected &&
          state.hasAllItems
      )

      return hasMatch ? selected : regular
    }

    // Same rule as matchesTopLengthFilter, applied to a raw product object (used when
    // checking category-tile availability against the `singles` product list directly).
    // product.length is always a non-empty string (derived via deriveLengthLabel), so
    // hasAlternateLength must be checked explicitly rather than relying on a falsy length.
    productMatchesTopLengthFilter(product) {
      if (window.TcSizePreferences.getSizeCategoryForProductType(product.productType) !== 'top')
        return true
      if (!product.hasAlternateLength) return true
      const productLength = product.length || ''
      if (!productLength) return true
      return this.normalizeSize(productLength) === this.normalizeSize(this.getEffectiveTopLength())
    }

    handleModalSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const modalButton of this.modalSizeButtons) {
        const isActive = modalButton === button
        modalButton.classList.toggle('active', isActive)
        modalButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedTopSize = size
    }

    handleModalWaistClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const btn of this.modalWaistButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedBottomWaist = size
    }

    handleModalLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const btn of this.modalLengthButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedBottomLength = size
    }

    handleModalTopLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopLength = size
      this.syncTopLengthButtons()
    }

    applyModalSize() {
      for (const sizeButton of this.sizeButtons) {
        const isActive =
          this.normalizeSize(sizeButton.dataset.size) === this.normalizeSize(this.selectedTopSize)
        sizeButton.classList.toggle('active', isActive)
        sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const btn of this.waistButtons) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomWaist)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const btn of this.lengthButtons) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomLength)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.syncTopLengthButtons()
      this.syncBottomSizeButtons()

      this.sizeCard?.classList.toggle('selected', !!this.selectedTopSize)
      this.updateBottomCardSelected()
      window.TcSizePreferences.commitSizePreference({
        top: this.selectedTopSize,
        bottomWaist: this.selectedBottomWaist,
        bottomLength: this.selectedBottomLength,
        topLength: this.selectedTopLength,
        bottomSize: this.selectedBottomSize,
      })
      this.resetSinglesPage()
      this.updateBrowse()
      const hasPendingStyle = !!this._pendingStyleHandle

      if (hasPendingStyle) {
        const handle = this._pendingStyleHandle
        const title = this._pendingStyleTitle || ''
        this._pendingStyleHandle = null
        this._pendingStyleTitle = null
        if (this.styleTiles) this.styleTiles.hidden = true
        if (this.styleResults) this.styleResults.hidden = false
        if (this.styleResultsTitle) this.styleResultsTitle.textContent = title
        for (const result of this.styleResults?.querySelectorAll('.category-result') || []) {
          result.hidden = result.dataset.categoryHandle !== handle
        }
        this.populateCategoryResult(handle)
        this.renderActiveStyleBundles()
        this.updateBrowse()
        this.closeSizeModal(false)
        this.styleBackButton?.focus()
      } else {
        this.closeSizeModal()
      }
    }

    async handlePackAdd(event) {
      const button = event.currentTarget
      const card = button.closest('[data-clearance-v3-pack-card]')
      const items = this.getSelectableItems(card, '[data-clearance-v3-pack-products]', true)
      await this.addItemsToCart(items, button, card, 'pack')
    }

    async handleSingleAdd(event) {
      const button = event.currentTarget
      const card = button.closest('[data-clearance-v3-single-card]')
      const items = this.getSelectableItems(card, '[data-clearance-v3-single-products]', false)
      await this.addItemsToCart(items, button, card, 'single')
    }

    setShowDealsReady(isReady) {
      if (!this.showDealsButton) return

      this.showDealsButton.disabled = !isReady
      this.showDealsButton.classList.toggle('ready', isReady)
      this.showDealsButton.setAttribute('aria-disabled', isReady ? 'false' : 'true')
    }

    saveSelectedSize() {
      try {
        const sizes = window.TcSizePreferences.commitSizePreference({
          top: this.selectedTopSize,
          bottomWaist: this.selectedBottomWaist,
          bottomLength: this.selectedBottomLength,
          topLength: this.selectedTopLength,
          bottomSize: this.selectedBottomSize,
        })
        document.dispatchEvent(
          new CustomEvent('tc:sizes-saved', {
            detail: { sizes },
          })
        )
      } catch (error) {
        console.warn('Unable to save clearance size selection', error)
      }
    }

    restoreSelectedSize() {
      try {
        const rawSizes = window.localStorage.getItem('tc_sizes')
        if (!rawSizes) return

        const sizes = JSON.parse(rawSizes)
        const topSize = sizes?.top || ''
        const bottomWaist = sizes?.bottomWaist || ''
        const bottomLength = sizes?.bottomLength || ''
        const topLength = sizes?.topLength || ''
        const bottomSize = sizes?.bottomSize || ''

        if (topSize) {
          const selectedButton = this.sizeButtons.find(
            (button) => this.normalizeSize(button.dataset.size) === this.normalizeSize(topSize)
          )
          if (selectedButton) {
            this.selectedTopSize = selectedButton.dataset.size || topSize
            this.sizeCard?.classList.add('selected')
            for (const sizeButton of this.sizeButtons) {
              const isActive = sizeButton === selectedButton
              sizeButton.classList.toggle('active', isActive)
              sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        if (bottomWaist) {
          const waistBtn = this.waistButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomWaist)
          )
          if (waistBtn) {
            this.selectedBottomWaist = waistBtn.dataset.size || bottomWaist
            for (const btn of this.waistButtons) {
              const isActive = btn === waistBtn
              btn.classList.toggle('active', isActive)
              btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        if (bottomLength) {
          const lengthBtn = this.lengthButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomLength)
          )
          if (lengthBtn) {
            this.selectedBottomLength = lengthBtn.dataset.size || bottomLength
            for (const btn of this.lengthButtons) {
              const isActive = btn === lengthBtn
              btn.classList.toggle('active', isActive)
              btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        // No stored value leaves it unset (no button preselected); getEffectiveTopLength()
        // still filters as 'Regular'.
        if (topLength) {
          const topLengthBtn = this.modalTopLengthButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(topLength)
          )
          if (topLengthBtn) {
            this.selectedTopLength = topLengthBtn.dataset.size || topLength
          }
        }
        this.syncTopLengthButtons()

        if (bottomSize) {
          const bottomSizeBtn = this.bottomSizeButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomSize)
          )
          if (bottomSizeBtn) this.selectedBottomSize = bottomSizeBtn.dataset.size || bottomSize
        }
        this.syncBottomSizeButtons()

        this.updateBottomCardSelected()

        if (this.isAnySizeSelected()) {
          this.setShowDealsReady(true)
          this.showStepTwo(false)
        }
      } catch (error) {
        console.warn('Unable to restore clearance size selection', error)
      }
    }

    parseTiers() {
      // The shared resolver lives in snippets/global-js.liquid, inline in the
      // head ahead of this deferred script. Guard anyway: if that script is ever
      // missing — a partial deploy, or a `theme dev` sync that lands assets
      // before snippets — the section should degrade, not throw and die.
      const fallbackTiers =
        typeof getClearanceDiscountTierList === 'function'
          ? getClearanceDiscountTierList()
          : [{ quantity: 1, percentage: 25 }]
      const script = this.querySelector('[data-clearance-v3-tiers]')
      if (!script) return fallbackTiers

      try {
        const tiers = JSON.parse(script.textContent || '[]')
          .map((tier) => ({
            quantity: Number(tier.quantity),
            percentage: Number(tier.percentage),
          }))
          .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity > 0)
          .sort((a, b) => a.quantity - b.quantity)

        return tiers.length ? tiers : fallbackTiers
      } catch (error) {
        console.warn('Unable to parse clearance tiers', error)
        return fallbackTiers
      }
    }

    // Base discount applies to all clearance items before any quantity tier is reached.
    parseBaseDiscount() {
      const parsed = Number(this.dataset.baseDiscount)
      if (Number.isFinite(parsed) && parsed >= 0) return parsed
      return typeof getClearanceBaseDiscount === 'function' ? getClearanceBaseDiscount() : 25
    }

    buildClearanceVariantIds() {
      const ids = new Set(this.clearanceVariantIds || [])
      const allCards = [...(this.singleCards || []), ...(this.packCards || [])]
      for (const card of allCards) {
        if (card.dataset.clearanceTagged !== 'true') continue
        const selectors = [
          '[data-clearance-v3-single-products]',
          '[data-clearance-v3-pack-products]',
        ]
        for (const selector of selectors) {
          for (const product of this.parseProducts(card, selector)) {
            for (const variant of product.variants || []) {
              if (variant.id) ids.add(Number(variant.id))
            }
          }
        }
      }
      return ids
    }

    async fetchAndRenderSingles() {
      const handle = this.dataset.clearanceCollection
      if (!handle) return

      const country = (this.dataset.country || '').toUpperCase()
      const language = (this.dataset.locale || '').toUpperCase()
      const context =
        country && language ? `@inContext(country: ${country}, language: ${language})` : ''

      // Non-US only: each variant's Fontana flag rides along; the US query is unchanged.
      const variantFontanaField = this.isFontanaSoldOutRegion()
        ? 'wh: metafield(namespace: "custom", key: "warehouse_inventory") { value }'
        : ''

      const QUERY = `
        query ClearanceSingles($handle: String!, $cursor: String) ${context} {
          collection(handle: $handle) {
            products(first: 250, after: $cursor, filters: [{ available: true }]) {
              pageInfo { hasNextPage endCursor }
              nodes {
                id handle title availableForSale onlineStoreUrl tags productType
                options { name }
                priceRange { minVariantPrice { amount } }
                compareAtPriceRange { minVariantPrice { amount } maxVariantPrice { amount } }
                featuredImage { url(transform: { maxWidth: 500 }) }
                metafields(identifiers: [
                  { namespace: "colorwaysV2", key: "color" }
                  { namespace: "pdp_app", key: "info" }
                ]) {
                  namespace key value
                  reference {
                    ... on Metaobject {
                      id
                      handle
                      productFamilyTitleField: field(key: "product_family_title") { value }
                      titleField: field(key: "title") { value }
                      familyNameField: field(key: "family_name") { value }
                      alternativeLengthFamilyField: field(key: "alternative_length_family") { value }
                      menuCategoryField: field(key: "menu_category") {
                        value
                        reference {
                          ... on Metaobject {
                            categoryNameField: field(key: "category_name") { value }
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 100) {
                  nodes {
                    id title availableForSale
                    price { amount }
                    compareAtPrice { amount }
                    selectedOptions { name value }
                    ${variantFontanaField}
                  }
                }
              }
            }
          }
        }
      `

      // v5: excludes dep_womens / dep_kids — bumped so cached v4 payloads (which still
      // contain them) are discarded rather than served for the rest of their TTL.
      // Keyed by country/language too: the payload holds @inContext prices in the
      // market's presentment currency, so a shared key served the previous market's
      // amounts for the rest of the TTL after a country switch.
      // v6: the cached product shape gained `selectTagged` for tag-based clearance
      // pricing. Entries written by v5 lack it, so every card would resolve as
      // non-select and never get the select discount. Bump the version whenever
      // normalizeSingle's output shape changes.
      // v10: the shape gained `familyName`, `menuCategoryId` and `menuCategoryName` for the
      // Choose style fallbacks. v9 entries lack them, so their products would skip layers 1–2.
      // v9: the shape gained `tags` and `familyHandle` for pack unit-term resolution.
      // v8: the shape gained `compareAtPrice` (product and variant), which the card
      // strikes through. v7 entries lack it and would strike through the product's
      // own price instead, showing a smaller saving than the shopper is getting.
      // v7: the shape gained `baseTagged`. Tag mode now resolves the rate from the
      // base and select discount tags alone, so v6 entries (which lack it) would
      // resolve every card as undiscounted.
      // v9: exclude products not published to Online Store; older caches can include
      // products available to the Storefront API that the Ajax cart cannot add.
      // Non-US payloads fold Fontana-OOS into `available`, so they get their own key
      // (`_wh`); the US key is untouched.
      const fontanaSuffix = this.isFontanaSoldOutRegion() ? '_wh' : ''
      const CACHE_KEY = `tc_clearance_singles_v10_${handle}_${country || 'XX'}_${language || 'XX'}${fontanaSuffix}`
      const CACHE_TTL = 5 * 60 * 1000

      try {
        const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY) || 'null')
        if (
          cached &&
          cached.ts &&
          Date.now() - cached.ts < CACHE_TTL &&
          Array.isArray(cached.data) &&
          cached.data.length &&
          cached.data.every((product) => product.handle) &&
          cached.data.every((product) => 'compareAtPrice' in product)
        ) {
          const script = this.querySelector('[data-clearance-products]')
          if (script) {
            const d = JSON.parse(script.textContent || '{}')
            d.singles = cached.data
            script.textContent = JSON.stringify(d)
          }
          this.isFetchingSingles = false
          this.renderSinglesGridFromJson()
          this.updateBrowse()
          this.maybeRevealMoreSingles()
          this.refreshCartSummary()
          return
        }
      } catch (e) {}

      this.isFetchingSingles = true
      // The grid starts empty (no SSR cards); updateBrowse swaps in the skeleton while
      // isFetchingSingles is set, so no "Loading more deals..." loader here.
      this.updateBrowse()

      const all = []
      let cursor = null

      try {
        do {
          const data = await this.fetchGraphQL(QUERY, { handle, cursor })
          const page = data?.data?.collection?.products
          if (!page) break

          const products = (page.nodes || [])
            .filter((n) => n.onlineStoreUrl && !this.isExcludedDepartment(n.tags))
            .map((n) => this.transformGraphQLProduct(n))
          all.push(...products)

          if (this.styleResults && !this.styleResults.hidden) {
            const activeResult = this.styleResults.querySelector('.category-result:not([hidden])')
            if (activeResult) {
              this.populateCategoryResult(activeResult.dataset.categoryHandle, all)
              this.updateBrowse()
            }
          }

          cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null
        } while (cursor)

        const script = this.querySelector('[data-clearance-products]')
        if (script && all.length) {
          const data = JSON.parse(script.textContent || '{}')
          data.singles = all
          script.textContent = JSON.stringify(data)
        }

        this.renderSinglesGridFromJson()

        if (all.length) {
          try {
            window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: all }))
          } catch (e) {}
        }
      } catch (e) {
        console.warn('Clearance V3: failed to fetch singles via GraphQL', e)
      } finally {
        this.isFetchingSingles = false
        if (this.styleSkeleton) this.styleSkeleton.hidden = true
        if (this.styleResults) this.styleResults.removeAttribute('aria-busy')
        if (this.styleResults && !this.styleResults.hidden) {
          const activeResult = this.styleResults.querySelector('.category-result:not([hidden])')
          if (activeResult) {
            this.populateCategoryResult(activeResult.dataset.categoryHandle)
          }
        }
        // Runs unconditionally: the singles grid needs its visibility/empty state
        // recomputed whether or not the style tab is open, and even if the fetch failed.
        this.updateBrowse()
        this.maybeRevealMoreSingles()
        this.refreshCartSummary()
      }
    }

    // All Fontana logic is non-US only; US is untouched (same gate as liquid-pdp.js).
    isFontanaSoldOutRegion() {
      return this.dataset.country !== 'US'
    }

    async fetchGraphQL(query, variables) {
      const endpoint = `${window.location.origin}/api/2025-07/graphql.json`
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': 'b7cddfa2ac815edc1ce3062ba667fe38',
        },
        body: JSON.stringify({ query, variables }),
      })
      if (!res.ok) throw new Error(`GraphQL request failed: ${res.status}`)
      return res.json()
    }

    // Department tags that don't belong in this (mens) clearance flow. Filtered out of the
    // singles list at fetch time rather than at render time, so they're excluded from the
    // Pick & Mix grid, the style rows, and the modal's stock checks alike. Storefront
    // `filters` can only match tags, not exclude them, so this has to happen client-side.
    isExcludedDepartment(tags) {
      const excluded = ['dep_womens', 'dep_kids']
      return (tags || []).some((tag) => excluded.includes(String(tag).trim().toLowerCase()))
    }

    transformGraphQLProduct(node) {
      const gidNum = (gid) =>
        Number(
          String(gid || '')
            .split('/')
            .pop()
        )

      const colorMF = (node.metafields || []).find(
        (m) => m && m.namespace === 'colorwaysV2' && m.key === 'color'
      )
      let color = ''
      if (colorMF?.value) {
        try {
          const parsed = JSON.parse(colorMF.value)
          color = Array.isArray(parsed) ? parsed.join(', ') : String(parsed)
        } catch {
          color = colorMF.value
        }
      }
      if (!color) color = node.title

      const pdpMF = (node.metafields || []).find(
        (m) => m && m.namespace === 'pdp_app' && m.key === 'info'
      )
      const pdpAppId = pdpMF?.reference?.id ? String(gidNum(pdpMF.reference.id)) : null
      const familyTitle =
        pdpMF?.reference?.productFamilyTitleField?.value ||
        pdpMF?.reference?.titleField?.value ||
        pdpMF?.reference?.familyNameField?.value ||
        node.title
      const length = this.deriveLengthLabel(familyTitle, pdpMF?.reference?.handle, node.title)
      const altLengthValue = pdpMF?.reference?.alternativeLengthFamilyField?.value
      const hasAlternateLength = !!altLengthValue && altLengthValue !== '[]' && altLengthValue !== 'null'
      // Both feed the Choose style fallbacks (syncStyleFallbackRows): family_name matches
      // versions of one style, menu_category is a grid_category GID that names its style tile.
      const familyName = pdpMF?.reference?.familyNameField?.value || ''
      const menuCategoryGid = pdpMF?.reference?.menuCategoryField?.value
      const menuCategoryId = menuCategoryGid ? String(gidNum(menuCategoryGid)) : ''
      const menuCategoryName =
        pdpMF?.reference?.menuCategoryField?.reference?.categoryNameField?.value || ''

      const price = Math.round(parseFloat(node.priceRange?.minVariantPrice?.amount || '0') * 100)
      // The struck-through "was" price. compareAtPriceRange's minimum counts a
      // variant without a compare-at as 0, so a partly marked-down product
      // collapses to 0 there — fall back to the maximum, then to the price
      // itself, mirroring card_compare in snippets/sale-page-deal-card.liquid.
      const compareAtMin = Math.round(
        parseFloat(node.compareAtPriceRange?.minVariantPrice?.amount || '0') * 100
      )
      const compareAtMax = Math.round(
        parseFloat(node.compareAtPriceRange?.maxVariantPrice?.amount || '0') * 100
      )
      let compareAtPrice = compareAtMin > 0 ? compareAtMin : compareAtMax
      if (!(compareAtPrice > price)) compareAtPrice = price
      // In tag mode these two tags alone decide the rate — the Clearance tag
      // does not — so a product carrying neither earns no clearance discount.
      // They do still mark the product as clearance for everything else.
      const selectTagName = (window.TC_CLEARANCE_CONFIG?.selectTag || '').toLowerCase()
      const baseTagName = (window.TC_CLEARANCE_CONFIG?.baseTag || '').toLowerCase()
      const clearanceTagNames = ['clearance', baseTagName, selectTagName].filter(Boolean)
      const clearanceTagged = (node.tags || []).some((t) =>
        clearanceTagNames.includes(t.toLowerCase())
      )
      const selectTagged =
        !!selectTagName && (node.tags || []).some((t) => t.toLowerCase() === selectTagName)
      const baseTagged =
        !!baseTagName && (node.tags || []).some((t) => t.toLowerCase() === baseTagName)

      const variants = (node.variants?.nodes || []).map((v) => ({
        id: gidNum(v.id),
        title: v.title,
        option1: v.selectedOptions?.[0]?.value ?? null,
        option2: v.selectedOptions?.[1]?.value ?? null,
        option3: v.selectedOptions?.[2]?.value ?? null,
        price: Math.round(parseFloat(v.price?.amount || '0') * 100),
        compareAtPrice: Math.round(parseFloat(v.compareAtPrice?.amount || '0') * 100),
        // Non-US: a Fontana-OOS variant is sold out (v.wh is only queried non-US)
        available:
          v.availableForSale &&
          !(this.isFontanaSoldOutRegion() && isFontanaOosMetafieldValue(v.wh?.value)),
      }))

      return {
        productId: gidNum(node.id),
        handle: node.handle,
        pdpAppId,
        title: node.title,
        familyTitle,
        familyHandle: pdpMF?.reference?.handle || '',
        familyName,
        menuCategoryId,
        menuCategoryName,
        color,
        price,
        compareAtPrice,
        available: node.availableForSale,
        tags: node.tags || [],
        clearanceTagged,
        selectTagged,
        baseTagged,
        image: node.featuredImage?.url ?? null,
        optionNames: (node.options || []).map((o) => o.name),
        variants,
        length,
        hasAlternateLength,
        productType: node.productType || '',
      }
    }

    // Mirrors snippets/your-cut-label.liquid: derives 'Short' / 'Regular' / 'Tall' from
    // the family title, metaobject handle, and product title. 'Regular' is assigned by
    // elimination since it never appears in the fields. 'Short Sleeve' and 'Shorts' are
    // stripped first so they can't be mistaken for the Short length.
    deriveLengthLabel(familyTitle, handle, productTitle) {
      let haystack = `${familyTitle || ''} ${handle || ''} ${productTitle || ''}`
      haystack = ` ${haystack.toLowerCase().replace(/-/g, ' ').replace(/_/g, ' ')} `
      haystack = haystack.replace(/short sleeve/g, ' ').replace(/shorts/g, ' ')
      if (haystack.includes(' tall ')) return 'Tall'
      if (haystack.includes(' short ')) return 'Short'
      return 'Regular'
    }

    parseAllProducts() {
      const script = this.querySelector('[data-clearance-products]')
      if (!script) return null
      try {
        return JSON.parse(script.textContent || '{}')
      } catch (e) {
        console.warn('Unable to parse clearance products data', e)
        return null
      }
    }

    renderPackCards(packs, packRow = null, context = 'deals') {
      const row = packRow || this.querySelector('[data-clearance-v3-pack-section] .pack-row')
      if (!row) return
      this.mutatePackRowPreservingScroll(row, () => {
        // Cards go in ahead of the server-rendered skeleton placeholders, not after, so the
        // row fills from the left and the shimmer keeps holding the remaining slots open
        // until syncPackPlaceholders clears them. Appending instead would paint one frame of
        // placeholders followed by the cards, i.e. the shift the skeleton exists to stop.
        const firstPlaceholder = row.querySelector('[data-clearance-v3-pack-placeholder]')
        const seenPackSignatures = new Set()
        for (const pack of packs) {
          if (!pack.productCount || !pack.totalPrice) continue
          // Metaobject packs can list the same products in different orders; treat those as one.
          const signature = this.getPackProductSignature(pack.products || [])
          if (signature && seenPackSignatures.has(signature)) continue
          if (signature) seenPackSignatures.add(signature)
          const card = this.createPackCardElement(pack, context)
          if (firstPlaceholder) row.insertBefore(card, firstPlaceholder)
          else row.appendChild(card)
        }
      })
    }

    // Order-insensitive multiset of product ids, so [A,B,C] and [C,A,B] collide.
    getPackProductSignature(products) {
      if (!Array.isArray(products) || !products.length) return ''

      return products
        .map((product) => String(product?.productId || ''))
        .filter(Boolean)
        .sort()
        .join(',')
    }

    // Cards are inserted ahead of whichever placeholder the track is snapped to, and the
    // browser answers that by keeping that element in view — which is why the carousel landed
    // mid-track on load instead of at the start. Snapping is switched off across the mutation
    // so no re-target happens, then the offset is put back where it actually was: zero on
    // first render, or wherever the shopper had scrolled to on later re-renders.
    mutatePackRowPreservingScroll(row, mutate) {
      if (!row) {
        mutate()
        return
      }

      const previousScrollLeft = row.scrollLeft
      row.style.scrollSnapType = 'none'
      try {
        mutate()
      } finally {
        row.scrollLeft = previousScrollLeft
        row.style.removeProperty('scroll-snap-type')
      }
    }

    // Tops the Quick Bundles carousel up towards PACK_ROW_TARGET_COUNT with 3-packs built the
    // same way the Choose style sub-rows build theirs (buildStyleBundle, one per style such as
    // Short Sleeves or Long Sleeves), so the row keeps filling once the pack metaobjects run
    // out. Sits behind the metaobject packs in the track: those are curated, these are derived.
    //
    // Called from updateBrowse ahead of the pricing and visibility passes, so the cards it adds
    // are picked up by the same loops as any other pack card — including the visible-count cap,
    // which is what stops generation from overflowing the row.
    syncGeneratedPackCards() {
      if (!this.packRow) return

      const bundles = this.buildGeneratedPackBundles()
      const signature = bundles
        .map(
          (bundle) =>
            `${bundle.name}:${this.getPackProductSignature(bundle.products)}`
        )
        .join('|')
      // updateBrowse runs on scroll, pagination and every size change, so bail unless the
      // derived set actually moved — rebuilding would drop the shopper's cart styling and
      // scroll offset for nothing.
      if (this.packRow.dataset.generatedSignature === signature) return
      this.packRow.dataset.generatedSignature = signature

      this.mutatePackRowPreservingScroll(this.packRow, () => {
        for (const card of this.packRow.querySelectorAll(
          '[data-clearance-v3-generated-pack]'
        )) {
          card.remove()
        }

        // Ahead of the placeholder block for the same reason renderPackCards is: the shimmer
        // holds the tail of the track until the fetch that feeds these lands.
        const firstPlaceholder = this.packRow.querySelector(
          '[data-clearance-v3-pack-placeholder]'
        )
        for (const bundle of bundles) {
          const card = this.createPackCardElement(bundle, 'generated')
          card.dataset.clearanceV3GeneratedPack = ''
          if (firstPlaceholder) this.packRow.insertBefore(card, firstPlaceholder)
          else this.packRow.appendChild(card)
        }
      })

      this.refreshPackCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
      this.syncCardCartState(this.cartVariantIds, this.cartVariantQuantities)
    }

    // One bundle per style sub-row, deduped by PDP app so the same style can't be derived twice
    // from two categories. Also skips packs whose product multiset already appears in a
    // curated metaobject pack (or an earlier generated pack), ignoring product order — otherwise
    // Quick Bundles can show the same 3 colors twice when a metaobject listed them differently.
    // Size matching is inherent — buildStyleBundle only keeps products with an orderable
    // variant in the shopper's size — and the length filter is applied to the candidate
    // products first, so a bundle is only built out of items in the selected Tops Length
    // (Regular when nothing is picked, per getEffectiveTopLength).
    buildGeneratedPackBundles() {
      const allProducts = this.parseAllProducts()
      const singles = allProducts?.singles || []
      if (!singles.length) return []

      const bundles = []
      const seenPdpAppIds = new Set()
      const seenPackSignatures = new Set()

      for (const pack of allProducts?.packs || []) {
        const signature = this.getPackProductSignature(pack.products || [])
        if (signature) seenPackSignatures.add(signature)
      }

      for (const subRow of this.querySelectorAll('[data-clearance-v3-style-row]')) {
        if (bundles.length >= PACK_ROW_TARGET_COUNT) break

        const pdpAppId = String(subRow.dataset.pdpAppId || '')
        if (!pdpAppId || seenPdpAppIds.has(pdpAppId) || !this.isStyleBundleEnabled(subRow)) {
          continue
        }
        // Choose style fallback rows stay out of Quick Bundles, so the Top Deals tab is
        // unchanged whether or not the fallbacks are enabled.
        if (subRow.hasAttribute('data-clearance-v3-fallback-row')) continue

        const products = singles.filter(
          (product) =>
            String(product.pdpAppId) === pdpAppId && this.productMatchesTopLengthFilter(product)
        )
        const bundle = this.buildStyleBundle(subRow, products)
        if (!bundle) continue

        const signature = this.getPackProductSignature(bundle.products)
        if (signature && seenPackSignatures.has(signature)) continue

        seenPdpAppIds.add(pdpAppId)
        if (signature) seenPackSignatures.add(signature)
        bundles.push(bundle)
      }

      return bundles
    }

    // The load skeleton, and only that. Generated bundles (syncGeneratedPackCards) are what
    // fill the row now, but they can't be built until the singles fetch lands — so the shimmer
    // holds the remaining slots until then and is cleared once the data is in, whether or not
    // the row reached PACK_ROW_TARGET_COUNT. Shimmer that outlived the fetch would read as a
    // stuck load rather than as pending content.
    //
    // Placeholders are purely visual: no [data-clearance-v3-pack-card] and no pack products
    // JSON, so refreshPackCardBindings, the cart count, and pricing never see them. Trimming
    // from the tail keeps them behind every real card, since both renderPackCards and
    // syncGeneratedPackCards insert ahead of the block and filtered-out cards collapse out of
    // the flex track.
    syncPackPlaceholders(visiblePackCount) {
      if (!this.packRow || !this.packPlaceholderTemplate) return

      const existing = Array.from(
        this.packRow.querySelectorAll('[data-clearance-v3-pack-placeholder]')
      )
      const needed = this.isFetchingSingles
        ? Math.max(0, PACK_ROW_TARGET_COUNT - visiblePackCount)
        : 0
      if (existing.length === needed) return

      this.mutatePackRowPreservingScroll(this.packRow, () => {
        for (let index = existing.length; index > needed; index--) existing[index - 1].remove()
        for (let index = existing.length; index < needed; index++) {
          this.packRow.appendChild(this.packPlaceholderTemplate.cloneNode(true))
        }
      })
    }

    refreshPackCardBindings() {
      this.packCards = Array.from(this.querySelectorAll('[data-clearance-v3-pack-card]'))
      this.addPackButtons = Array.from(this.querySelectorAll('[data-clearance-v3-add-pack]'))

      for (const button of this.addPackButtons) {
        button.removeEventListener('click', this.handlePackAdd)
        button.addEventListener('click', this.handlePackAdd)
      }
    }

    renderActiveStyleBundles() {
      if (this.isFetchingSingles || !this.styleResults || this.styleResults.hidden) return

      const result = this.styleResults.querySelector('.category-result:not([hidden])')
      if (!result) {
        this.clearStyleBundles()
        return
      }

      const singles = this.parseAllProducts()?.singles || []
      this.renderStyleBundlesForResult(result, singles)
    }

    /* A/B test concluded: automatic bundle generation retained for reference.
    renderAutomaticBundles() {
      if (this.isFetchingSingles || !this.automaticBundlesGrid) return

      const singles = this.parseAllProducts()?.singles || []
      const bundles = []
      const maxAutomaticBundles = 10
      const seenPdpAppIds = new Set()

      for (const subRow of this.querySelectorAll('[data-clearance-v3-style-row]')) {
        const pdpAppId = String(subRow.dataset.pdpAppId || '')
        if (!pdpAppId || seenPdpAppIds.has(pdpAppId) || !this.isStyleBundleEnabled(subRow)) {
          continue
        }

        const products = singles.filter(
          (product) => String(product.pdpAppId) === pdpAppId
        )
        const bundle = this.buildStyleBundle(subRow, products)
        if (!bundle) continue

        seenPdpAppIds.add(pdpAppId)
        bundles.push(bundle)
      }

      const visibleBundles = bundles.slice(0, maxAutomaticBundles)
      if (this.automaticBundlesShopAll) {
        this.automaticBundlesShopAll.hidden = bundles.length <= maxAutomaticBundles
      }

      const signature = visibleBundles
        .map(
          (bundle) =>
            `${bundle.name}:${bundle.products.map((product) => product.productId).join(',')}`
        )
        .join('|')
      if (this.automaticBundlesGrid.dataset.bundleSignature === signature) {
        this.setAutomaticBundlesLoading(false)
        return
      }

      const fragment = document.createDocumentFragment()
      for (const bundle of visibleBundles) {
        fragment.appendChild(this.createPackCardElement(bundle, 'automatic'))
      }

      this.automaticBundlesGrid.replaceChildren(fragment)
      this.automaticBundlesGrid.dataset.bundleSignature = signature
      this.setAutomaticBundlesLoading(false)
      if (this.automaticBundlesEmpty) this.automaticBundlesEmpty.hidden = bundles.length > 0

      this.refreshPackCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
      this.syncCardCartState(this.cartVariantIds, this.cartVariantQuantities)
    }

    setAutomaticBundlesLoading(isLoading) {
      if (this.automaticBundlesLoading) this.automaticBundlesLoading.hidden = !isLoading
      if (this.automaticBundlesGrid) {
        this.automaticBundlesGrid.setAttribute('aria-busy', isLoading ? 'true' : 'false')
      }
    }
    */

    clearStyleBundles() {
      for (const slot of this.querySelectorAll('[data-clearance-v3-style-bundle-slot]')) {
        slot.textContent = ''
        slot.hidden = true
      }
      this.refreshPackCardBindings()
    }

    renderStyleBundlesForResult(result, singles) {
      if (!result) return

      for (const subRow of result.querySelectorAll('[data-clearance-v3-style-row]')) {
        const pdpAppId = subRow.dataset.pdpAppId
        if (!pdpAppId) continue

        const products = singles.filter(
          (product) => String(product.pdpAppId) === String(pdpAppId)
        )
        this.renderStyleBundleForSubRow(subRow, products)
      }

      this.refreshPackCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
    }

    renderStyleBundleForSubRow(subRow, products) {
      const itemsContainer = subRow?.querySelector('[data-clearance-v3-style-row-items]')
      const bundleSlot = subRow?.querySelector('[data-clearance-v3-style-bundle-slot]')
      if (!itemsContainer || !bundleSlot) return

      if (!this.isStyleBundleEnabled(subRow)) {
        bundleSlot.textContent = ''
        bundleSlot.hidden = true
        return
      }

      const existingBundleCard = bundleSlot.querySelector('[data-clearance-v3-style-bundle-card]')
      const bundle = this.buildStyleBundle(subRow, products)
      if (!bundle) {
        bundleSlot.textContent = ''
        bundleSlot.hidden = true
        return
      }

      const signature = `${bundle.name}:${bundle.products
        .map((product) => product.productId)
        .join(',')}`

      if (existingBundleCard?.dataset.bundleSignature === signature) {
        bundleSlot.hidden = false
        return
      }

      const bundleCard = this.createPackCardElement(bundle, 'style')
      bundleCard.dataset.clearanceV3StyleBundleCard = ''
      bundleCard.dataset.bundleSignature = signature

      bundleSlot.replaceChildren(bundleCard)
      bundleSlot.hidden = false
    }

    buildStyleBundle(subRow, products) {
      const selectableProducts = []
      for (const product of products) {
        if (this.getVariantForSelectedSize(product)) selectableProducts.push(product)
      }

      const packProducts = this.getStyleBundleProducts(selectableProducts)
      if (!packProducts.length) return null

      const totalPrice = packProducts.reduce((total, product) => {
        const variant = this.getVariantForSelectedSize(product)
        return total + Number(variant?.price || product.price || 0)
      }, 0)

      return {
        name: `${this.getStyleBundleName(subRow)} 3-Pack`,
        totalPrice,
        productCount: packProducts.length,
        colorList: this.formatPackColorList(packProducts),
        products: packProducts,
      }
    }

    isStyleBundleEnabled(subRow) {
      const categoryResult = subRow?.closest('[data-clearance-v3-auto-bundle-enabled]')
      return categoryResult?.dataset.clearanceV3AutoBundleEnabled !== 'false'
    }

    getStyleBundleProducts(products) {
      if (!products.length) return []
      if (products.length === 1) return [products[0], products[0], products[0]]
      if (products.length === 2) return [products[0], products[0], products[1]]

      return products.slice(0, 3)
    }

    getStyleBundleName(subRow) {
      const rowTitle = subRow
        ?.querySelector('[data-clearance-v3-style-row-title]')
        ?.textContent?.trim()

      return rowTitle || 'Products'
    }

    formatPackColorList(products, fallback = '') {
      const colors = Array.isArray(products)
        ? products.map((product) => String(product?.color || '').trim()).filter(Boolean)
        : []

      if (!colors.length) return fallback

      const counts = new Map()
      const orderedColors = []
      for (const color of colors) {
        if (!counts.has(color)) orderedColors.push(color)
        counts.set(color, (counts.get(color) || 0) + 1)
      }

      return orderedColors
        .map((color) => {
          const count = counts.get(color) || 0
          return count > 1 ? `${count}x ${color}` : color
        })
        .join(' | ')
    }

    hasUnitTermSignal(signals, terms) {
      for (const term of terms) {
        if (signals.includes(` ${term} `)) return true
      }
      return false
    }

    hasUnitTermTag(tags, terms) {
      for (const term of terms) {
        if (tags.has(term)) return true
      }
      return false
    }

    getPackUnitTerm(pack) {
      const products = Array.isArray(pack?.products) ? pack.products : []
      const productSignals = [pack?.name]
      const productTags = new Set()
      for (const product of products) {
        productSignals.push(
          product?.title,
          product?.familyTitle,
          product?.familyHandle,
          product?.productType
        )
        if (!Array.isArray(product?.tags)) continue
        for (const tag of product.tags) productTags.add(String(tag).trim().toLowerCase())
      }
      const signals = ` ${productSignals
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()} `
      const boxerSignals = `${signals} ${Array.from(productTags).join(' ')}`

      if (this.hasUnitTermSignal(signals, ['set', 'sets'])) return 'set'
      if (
        this.hasUnitTermSignal(signals, ['hoodie', 'hoodies', 'sweatshirt', 'sweatshirts']) &&
        this.hasUnitTermSignal(signals, ['jogger', 'joggers'])
      ) {
        return 'set'
      }
      if (
        this.hasUnitTermSignal(signals, ['hoodie', 'hoodies', 'sweatshirt', 'sweatshirts']) ||
        this.hasUnitTermTag(productTags, ['hoodie', 'hoodies'])
      ) {
        return 'hoodie'
      }
      if (
        this.hasUnitTermSignal(
          signals,
          ['jacket', 'jackets', 'blazer', 'blazers', 'coat', 'coats', 'shacket', 'shackets']
        ) ||
        this.hasUnitTermTag(productTags, ['jacket', 'jackets'])
      ) {
        return 'jacket'
      }
      if (
        this.hasUnitTermSignal(signals, ['vest', 'vests']) ||
        this.hasUnitTermTag(productTags, ['vest', 'vests'])
      ) {
        return 'vest'
      }
      if (
        this.hasUnitTermSignal(signals, ['polo', 'polos']) ||
        this.hasUnitTermTag(productTags, ['polo', 'polos'])
      ) {
        return 'polo'
      }
      if (
        this.hasUnitTermSignal(signals, ['henley', 'henleys']) ||
        this.hasUnitTermTag(productTags, ['henley', 'henleys'])
      ) {
        return 'henley'
      }
      if (
        this.hasUnitTermSignal(boxerSignals, [
          'boxer',
          'boxers',
          'trunk',
          'trunks',
          'brief',
          'briefs',
        ])
      ) {
        return 'boxer'
      }
      if (this.hasUnitTermSignal(signals, ['cap', 'caps', 'hat', 'hats'])) return 'cap'
      if (
        (this.hasUnitTermSignal(signals, ['dress', 'dresses']) ||
          this.hasUnitTermTag(productTags, ['dress', 'dresses'])) &&
        !this.hasUnitTermSignal(signals, ['dress shirt', 'dress shirts'])
      ) {
        return 'dress'
      }
      if (
        this.hasUnitTermSignal(signals, [
          'bottom',
          'bottoms',
          'pant',
          'pants',
          'jean',
          'jeans',
          'chino',
          'chinos',
          'jogger',
          'joggers',
          'trouser',
          'trousers',
          'shorts',
          'sock',
          'socks',
        ]) ||
        this.hasUnitTermTag(productTags, ['bottoms'])
      ) {
        return 'pair'
      }
      if (
        this.hasUnitTermSignal(signals, [
          'shirt',
          'shirts',
          'tee',
          'tees',
          't shirt',
          't shirts',
          'polo',
          'polos',
          'henley',
          'henleys',
          'tank',
          'tanks',
          'button up',
          'button ups',
          'short sleeve',
          'long sleeve crew',
          'sweater',
          'sweaters',
        ]) ||
        this.hasUnitTermTag(productTags, ['shirt', 'shirts', 'tee', 'tees'])
      ) {
        return 'shirt'
      }

      return 'item'
    }

    updatePackPerPrice(element, formattedPrice, unitTerm) {
      if (!element) return

      const label = document.createElement('span')
      label.className = 'pcard-per__label'
      label.textContent = `/${unitTerm || 'item'}`
      element.replaceChildren(document.createTextNode(formattedPrice), label)
    }

    createPackCardElement(pack, context = 'deals') {
      const isStyleBundle = context === 'style'
      const titleId = this.getNextCardTitleId('pack')
      const article = document.createElement('article')
      article.className = isStyleBundle ? 'pcard pcard--style-bundle' : 'pcard'
      article.setAttribute('aria-labelledby', titleId)
      article.dataset.clearanceV3PackCard = ''
      article.dataset.clearanceV3PackContext = context
      article.dataset.basePrice = String(pack.totalPrice)
      article.dataset.packCount = String(pack.productCount)
      article.dataset.packLength = pack.packLength || ''
      article.dataset.clearanceV3UnitTerm = this.getPackUnitTerm(pack)

      const imgsDiv = document.createElement('div')
      imgsDiv.className = !isStyleBundle && pack.productCount > 4 ? 'pcard-imgs m6' : 'pcard-imgs'
      if (isStyleBundle) {
        const badgeDiv = document.createElement('div')
        badgeDiv.className = 'pcard-badge'
        badgeDiv.textContent = '3-Pack'
        imgsDiv.appendChild(badgeDiv)
      }
      const visibleProducts = pack.products.slice(0, isStyleBundle ? 3 : 6)
      for (let i = 0; i < visibleProducts.length; i++) {
        const p = visibleProducts[i]
        if (!p.image) continue
        const img = document.createElement('img')
        img.src = p.image
        img.alt = p.color || p.title || pack.name || ''
        img.loading = 'lazy'
        img.width = 360
        img.height = 480
        img.dataset.productIndex = String(i)
        imgsDiv.appendChild(img)
      }
      article.appendChild(imgsDiv)

      const bodyDiv = document.createElement('div')
      bodyDiv.className = 'pcard-body'

      const nameDiv = document.createElement('h3')
      nameDiv.className = 'pcard-name'
      nameDiv.id = titleId
      nameDiv.textContent = pack.name || ''
      bodyDiv.appendChild(nameDiv)

      const colorsDiv = document.createElement('div')
      colorsDiv.className = 'pcard-colors'
      colorsDiv.textContent = this.formatPackColorList(pack.products, pack.colorList || '')
      bodyDiv.appendChild(colorsDiv)

      // Per-item price leads the pricing block — it's the headline number on a pack card,
      // with the pack total and its strike-through original reading as support beneath it.
      const perDiv = document.createElement('div')
      perDiv.className = 'pcard-per'
      perDiv.dataset.clearanceV3PackPer = ''
      const perPrice =
        pack.productCount > 0 ? Math.round(pack.totalPrice / pack.productCount) : pack.totalPrice
      this.updatePackPerPrice(
        perDiv,
        this.formatMoney(perPrice),
        article.dataset.clearanceV3UnitTerm
      )
      bodyDiv.appendChild(perDiv)

      const pricingDiv = document.createElement('div')
      pricingDiv.className = 'pcard-pricing'
      const nowSpan = document.createElement('span')
      nowSpan.className = 'pcard-now'
      nowSpan.dataset.clearanceV3PackPrice = ''
      nowSpan.textContent = this.formatMoney(pack.totalPrice)
      const wasSpan = document.createElement('span')
      wasSpan.className = 'pcard-was'
      wasSpan.dataset.clearanceV3PackWas = ''
      wasSpan.hidden = true
      wasSpan.textContent = this.formatMoney(pack.totalPrice)
      const discSpan = document.createElement('span')
      discSpan.className = 'pcard-disc'
      discSpan.dataset.clearanceV3PackDiscount = ''
      discSpan.hidden = true
      discSpan.textContent = '0% OFF'
      pricingDiv.appendChild(this.createSrOnlySpan('Sale price'))
      pricingDiv.appendChild(nowSpan)
      pricingDiv.appendChild(this.createSrOnlySpan('Original price'))
      pricingDiv.appendChild(wasSpan)
      pricingDiv.appendChild(discSpan)
      bodyDiv.appendChild(pricingDiv)

      const addBtn = document.createElement('button')
      addBtn.className = 'pcard-btn'
      addBtn.type = 'button'
      addBtn.dataset.clearanceV3AddPack = ''
      addBtn.dataset.defaultLabel = 'Add All ' + pack.productCount
      addBtn.dataset.addedLabel = 'Added'
      addBtn.dataset.productTitle = pack.name || 'bundle'
      this.updateButtonLabel(addBtn, addBtn.dataset.defaultLabel)
      bodyDiv.appendChild(addBtn)

      const packScriptEl = document.createElement('script')
      packScriptEl.type = 'application/json'
      packScriptEl.dataset.clearanceV3PackProducts = ''
      packScriptEl.textContent = JSON.stringify(pack.products)
      bodyDiv.appendChild(packScriptEl)

      article.appendChild(bodyDiv)
      return article
    }

    createSingleCardElement(product, context = 'deals') {
      const isStyleContext = context === 'style'
      const titleId = this.getNextCardTitleId('single')
      const article = document.createElement('article')
      article.className = 'card'
      article.setAttribute('aria-labelledby', titleId)
      article.dataset.clearanceV3SingleCard = ''
      article.dataset.clearanceV3CardContext = context
      article.dataset.productId = String(product.productId)
      article.dataset.productHandle = product.handle || ''
      article.dataset.basePrice = String(product.price)
      article.dataset.clearanceCompareAtPrice = String(product.compareAtPrice || product.price)
      if (product.clearanceTagged) article.dataset.clearanceTagged = 'true'
      if (product.selectTagged) article.dataset.clearanceSelectTagged = 'true'
      if (product.baseTagged) article.dataset.clearanceBaseTagged = 'true'
      article.dataset.isNumericSizing = (product.optionNames || []).includes('Waist')
        ? 'true'
        : 'false'
      article.dataset.productType = product.productType || ''
      article.dataset.hasAlternateLength = product.hasAlternateLength ? 'true' : 'false'
      if (
        window.TcSizePreferences.getSizeCategoryForProductType(product.productType) === 'top' &&
        product.hasAlternateLength
      ) {
        article.dataset.length = product.length || ''
      }

      const quickAddButton = document.createElement('button')
      quickAddButton.className = 'card-quick-add'
      quickAddButton.type = 'button'
      quickAddButton.dataset.clearanceV3QuickAdd = ''
      quickAddButton.setAttribute('aria-labelledby', titleId)
      quickAddButton.setAttribute('aria-haspopup', 'dialog')
      article.appendChild(quickAddButton)

      if (product.image) {
        const img = document.createElement('img')
        img.className = 'card-img'
        img.src = product.image
        img.alt = ((product.color || '') + ' ' + (product.title || '')).trim()
        img.loading = 'lazy'
        img.width = 500
        img.height = 667
        article.appendChild(img)
      }

      const bodyDiv = document.createElement('div')
      bodyDiv.className = 'card-body'

      const topDiv = document.createElement('div')
      topDiv.className = 'card-body__top'

      const colorDiv = document.createElement('h3')
      colorDiv.className = 'card-color'
      colorDiv.id = titleId
      colorDiv.textContent = isStyleContext
        ? product.color || ''
        : product.familyTitle || product.title || ''
      topDiv.appendChild(colorDiv)

      const typeDiv = document.createElement('div')
      typeDiv.className = 'card-type'
      if (isStyleContext) {
        typeDiv.textContent = product.familyTitle || product.title || ''
        const dotSpan = document.createElement('span')
        dotSpan.setAttribute('aria-hidden', 'true')
        dotSpan.textContent = '\u00A0·\u00A0'
        typeDiv.appendChild(dotSpan)
      } else {
        typeDiv.textContent = product.color || ''
        const dotSpan = document.createElement('span')
        dotSpan.setAttribute('aria-hidden', 'true')
        dotSpan.textContent = '\u00A0·\u00A0'
        typeDiv.appendChild(dotSpan)
      }
      const sizeSpan = document.createElement('span')
      sizeSpan.dataset.clearanceV3CardSize = ''
      typeDiv.appendChild(sizeSpan)
      topDiv.appendChild(typeDiv)
      bodyDiv.appendChild(topDiv)

      const bottomDiv = document.createElement('div')
      bottomDiv.className = 'card-body__bottom'

      const pricesDiv = document.createElement('div')
      pricesDiv.className = 'card-prices'
      const priceSpan = document.createElement('span')
      priceSpan.className = 'card-price'
      priceSpan.dataset.clearanceV3CardPrice = ''
      priceSpan.textContent = this.formatMoney(product.price)
      const origSpan = document.createElement('span')
      origSpan.className = 'card-orig'
      origSpan.textContent = this.formatMoney(product.price)
      pricesDiv.appendChild(this.createSrOnlySpan('Sale price'))
      pricesDiv.appendChild(priceSpan)
      pricesDiv.appendChild(this.createSrOnlySpan('Original price'))
      pricesDiv.appendChild(origSpan)
      if (product.clearanceTagged || product.selectTagged || product.baseTagged) {
        // Hides the plain .card-orig price, so only one struck-through price shows.
        article.dataset.clearancePriced = 'true'
        const cwasSpan = document.createElement('span')
        cwasSpan.className = 'pcard-was'
        cwasSpan.dataset.clearanceV3ClearanceWas = ''
        cwasSpan.hidden = true
        const cdiscSpan = document.createElement('span')
        cdiscSpan.className = 'pcard-disc'
        cdiscSpan.dataset.clearanceV3ClearanceDisc = ''
        cdiscSpan.hidden = true
        pricesDiv.appendChild(cwasSpan)
        pricesDiv.appendChild(cdiscSpan)
      }
      topDiv.appendChild(pricesDiv)

      const addBtn = document.createElement('button')
      addBtn.className = 'card-add'
      addBtn.type = 'button'
      addBtn.dataset.clearanceV3AddSingle = ''
      addBtn.dataset.defaultLabel = 'Add'
      addBtn.dataset.addedLabel = 'Added'
      addBtn.dataset.productTitle = product.title || ''
      this.updateButtonLabel(addBtn, 'Add')
      bottomDiv.appendChild(addBtn)
      bodyDiv.appendChild(bottomDiv)

      const singleScriptEl = document.createElement('script')
      singleScriptEl.type = 'application/json'
      singleScriptEl.dataset.clearanceV3SingleProducts = ''
      singleScriptEl.textContent = JSON.stringify([
        {
          productId: product.productId,
          handle: product.handle,
          title: product.title,
          familyTitle: product.familyTitle,
          color: product.color,
          price: product.price,
          available: product.available,
          // Needed to tell letter-sized bottoms from tops (getBottomSizeOptionIndex).
          optionNames: product.optionNames || [],
          tags: product.tags || [],
          productType: product.productType || '',
          variants: product.variants,
        },
      ])
      bodyDiv.appendChild(singleScriptEl)

      article.appendChild(bodyDiv)
      return article
    }

    getNextCardTitleId(cardType) {
      this.cardIdCounter += 1
      const sectionId = this.dataset.sectionId || 'section'
      return `ClearanceDynamicCardTitle-${sectionId}-${cardType}-${this.cardIdCounter}`
    }

    refreshSingleCardBindings() {
      this.singleCards = Array.from(this.querySelectorAll('[data-clearance-v3-single-card]'))
      for (const card of this.singleCards) this.stripLengthIfNotEligibleForLengthFilter(card)
      this.addSingleButtons = Array.from(this.querySelectorAll('[data-clearance-v3-add-single]'))

      for (const button of this.addSingleButtons) {
        button.removeEventListener('click', this.handleSingleAdd)
        button.addEventListener('click', this.handleSingleAdd)
      }
    }

    // Rebuilds the "Pick & Mix" grid from the `singles` array in [data-clearance-products]
    // (the full collection, fetched via the Storefront API) rather than a server-rendered
    // first page — so size/length filtering considers every product, not just the first 50.
    // The grid holds only 'deals' cards; style-tab cards live in their own sub-rows.
    renderSinglesGridFromJson() {
      if (!this.singlesGrid) return

      const singles = this.parseAllProducts()?.singles || []
      const fragment = document.createDocumentFragment()
      for (const product of singles) {
        fragment.appendChild(this.createSingleCardElement(product, 'deals'))
      }

      this.singlesGrid.replaceChildren(fragment)
      this.refreshSingleCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
      this.resetSinglesPage()
    }

    parseProducts(card, selector) {
      if (!card) return []

      if (!card.clearanceV3Products) {
        card.clearanceV3Products = {}
      }

      if (card.clearanceV3Products[selector]) {
        return card.clearanceV3Products[selector]
      }

      const script = card.querySelector(selector)
      if (!script) return []

      try {
        const products = JSON.parse(script.textContent || '[]')
        card.clearanceV3Products[selector] = products
        return products
      } catch (error) {
        console.warn('Unable to parse clearance products', error)
        card.clearanceV3Products[selector] = []
        return []
      }
    }

    getSelectableItems(card, selector, requireAllProducts) {
      const products = this.parseProducts(card, selector)
      if (!products.length) return []

      const items = []
      for (const product of products) {
        if (!this.isProductAvailable(product)) {
          if (requireAllProducts) return []
          continue
        }

        const variant = this.getVariantForSelectedSize(product)
        if (!variant && requireAllProducts) return []
        if (variant) items.push({ product, variant })
      }

      return items
    }

    hasSelectableItems(card, selector, requireAllProducts) {
      const products = this.parseProducts(card, selector)
      if (!products.length) return false

      let selectableCount = 0
      for (const product of products) {
        if (!this.isProductAvailable(product)) continue

        if (this.getVariantForSelectedSize(product)) {
          selectableCount += 1
        }
      }

      return requireAllProducts ? selectableCount === products.length : selectableCount > 0
    }

    isProductAvailable(product) {
      if (product?.available === false) return false

      const variants = Array.isArray(product?.variants) ? product.variants : []
      return variants.some((variant) => variant.available)
    }

    getVariantForSelectedSize(product) {
      if (!this.isProductAvailable(product)) return null

      const variants = Array.isArray(product?.variants) ? product.variants : []
      if (!variants.length) return null

      if (variants.length === 1) {
        const variant = variants[0]
        if (variant.available) return variant
      }

      // Letter-sized bottoms match on the Bottoms size only, never the top size.
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      if (bottomSizeOptionIndex >= 0)
        return this.getVariantForSelectedBottomSize(product, bottomSizeOptionIndex)

      // 1. Try top size
      if (this.selectedTopSize) {
        for (const variant of variants) {
          if (!variant.available) continue
          if (this.variantMatchesSize(variant, this.selectedTopSize)) return variant
        }
      }

      // 2+3. Try bottom size — single-option: waist only; two-option: waist + length
      for (const variant of variants) {
        if (!variant.available) continue
        const hasTwoOptions =
          variant.option2 && this.normalizeSize(variant.option2) !== 'DEFAULTTITLE'
        if (!hasTwoOptions) {
          if (
            this.selectedBottomWaist &&
            this.variantMatchesWaist(variant, this.selectedBottomWaist)
          )
            return variant
        } else {
          if (
            this.selectedBottomWaist &&
            this.selectedBottomLength &&
            this.variantMatchesBottomSize(
              variant,
              this.selectedBottomWaist,
              this.selectedBottomLength
            )
          )
            return variant
        }
      }

      return null
    }

    // Boxers/underwear aren't `bottoms`-tagged but are sized from the Bottoms preference.
    isBottomsSizedProductType(productType) {
      return /boxer|underwear/i.test(String(productType || ''))
    }

    // Card JSON carries `isBottomsTagged`; GraphQL-shaped products carry raw `tags`.
    getBottomSizeOptionIndex(product) {
      const { getBottomSizeOptionIndex, hasBottomsTag } = window.TcSizePreferences
      const isBottomsTagged =
        (product?.isBottomsTagged ?? hasBottomsTag(product?.tags)) ||
        this.isBottomsSizedProductType(product?.productType)
      return getBottomSizeOptionIndex(product?.optionNames, isBottomsTagged)
    }

    // Letter-sized bottoms only count toward a top size through their own Tops/Size
    // option (sets) — a bottoms "M" must not keep the modal's top "M" visible.
    doesProductHaveTopSizeStock(product, size) {
      const variants = product?.variants || []
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      if (bottomSizeOptionIndex < 0)
        return variants.some((v) => v.available && this.variantMatchesSize(v, size))

      const { getTopSizeOptionIndex, doesVariantMatchTopSizeOption } = window.TcSizePreferences
      const topSizeOptionIndex = getTopSizeOptionIndex(product.optionNames, bottomSizeOptionIndex)
      if (topSizeOptionIndex < 0) return false
      return variants.some(
        (v) => v.available && doesVariantMatchTopSizeOption(v, topSizeOptionIndex, size)
      )
    }

    // On a Size + Bottoms set both sizes must be picked; with only one there's no full variant.
    getVariantForSelectedBottomSize(product, bottomSizeOptionIndex) {
      if (!this.selectedBottomSize) return null

      const { getTopSizeOptionIndex, doesVariantMatchBottomSize, doesVariantMatchTopSizeOption } =
        window.TcSizePreferences
      const topSizeOptionIndex = getTopSizeOptionIndex(product.optionNames, bottomSizeOptionIndex)
      const isSet = topSizeOptionIndex >= 0
      if (isSet && !this.selectedTopSize) return null

      return (
        product.variants.find(
          (variant) =>
            variant.available &&
            doesVariantMatchBottomSize(variant, bottomSizeOptionIndex, this.selectedBottomSize) &&
            (!isSet ||
              doesVariantMatchTopSizeOption(variant, topSizeOptionIndex, this.selectedTopSize))
        ) || null
      )
    }

    variantMatchesWaist(variant, waist) {
      const normWaist = this.normalizeSize(waist)
      const opts = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .map((o) => this.normalizeSize(o))
      return opts.some((o) => o === normWaist || o === `W${normWaist}`)
    }

    variantMatchesBottomSize(variant, waist, length) {
      const normWaist = this.normalizeSize(waist)
      const normLength = this.normalizeSize(length)
      const lengthNum = normLength.replace(/L$/, '')

      const opts = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .map((o) => this.normalizeSize(o))

      const hasWaist = opts.some((o) => o === normWaist || o === `W${normWaist}`)
      const hasLength = opts.some(
        (o) => o === normLength || o === lengthNum || o === `${lengthNum}L`
      )
      if (hasWaist && hasLength) return true

      const title = this.normalizeSize(variant.title || '')
      const parts = title.split(/[X\/,\-\s]+/)
      if (parts.length >= 2) {
        const tWaist = parts[0].replace(/^W/, '')
        const tLength = parts[1].replace(/^L/, '').replace(/L$/, '')
        if (tWaist === normWaist && (tLength === lengthNum || parts[1] === normLength))
          return true
      }

      return false
    }

    variantMatchesSize(variant, size) {
      const aliases = this.getSizeAliases(size).map((alias) => this.normalizeSize(alias))
      const values = [variant.option1, variant.option2, variant.option3, variant.title].filter(
        Boolean
      )

      for (const value of values) {
        const normalizedValue = this.normalizeSize(value)
        if (aliases.includes(normalizedValue)) return true

        const valueParts = normalizedValue
          .split(/[\/,-]/)
          .map((part) => part.trim())
          .filter(Boolean)

        for (const part of valueParts) {
          if (aliases.includes(part)) return true
        }
      }

      return false
    }

    getSizeAliases(size) {
      return window.TcSizePreferences.getTopSizeAliases(size)
    }

    normalizeSize(value) {
      return window.TcSizePreferences.normalizeSize(value)
    }

    getSelectedSizeSummary() {
      const parts = []

      if (this.selectedTopSize) {
        parts.push(this.selectedTopSize)
      }

      if (this.selectedBottomSize) {
        parts.push(`Bottoms ${this.selectedBottomSize}`)
      }

      if (this.selectedBottomWaist && this.selectedBottomLength) {
        parts.push(`W${this.selectedBottomWaist} x ${this.selectedBottomLength}`)
      }

      return parts.join(' · ') || 'Edit size'
    }

    updateSelectedSizeLabel() {
      if (!this.selectedSizeLabel) return

      this.selectedSizeLabel.textContent = this.getSelectedSizeSummary()
    }

    updateBrowse() {
      this.updateSelectedSizeLabel()
      if (!this.isFetchingSingles) {
        this.renderActiveStyleBundles()
        // Before the pricing and visibility passes below, so the bundles it derives are
        // priced, filtered and capped as ordinary pack cards.
        this.syncGeneratedPackCards()
        // A/B test concluded: this.renderAutomaticBundles()
      }
      this.updatePackPricing()
      this.updateSinglePricing()

      const isDealsActive = this.activeTab === 'deals'
      const isStyleResultsActive =
        !isDealsActive && this.styleResults && !this.styleResults.hidden
      let visiblePackCount = 0
      let visibleSingleCount = 0

      if (this.dealsView) {
        this.dealsView.hidden = !isDealsActive
      }

      if (this.styleView) {
        this.styleView.hidden = isDealsActive
      }

      // Counts curated metaobject packs and generated bundles against one budget, so whichever
      // mix survives the filter the row tops out at the same number of cards.
      const maxVisibleDealsPacks = PACK_ROW_TARGET_COUNT

      // Availability is resolved up front because the pack-length fallback needs to know
      // whether any pack in the selected length is actually orderable before deciding
      // which length to show (see resolvePackLengthFilter).
      const packStates = (this.packCards || []).map((card) => {
        const packProducts = this.parseProducts(card, '[data-clearance-v3-pack-products]')
        const selectedItems = this.getSelectableItems(
          card,
          '[data-clearance-v3-pack-products]',
          true
        )
        return {
          card,
          packProducts,
          selectedItems,
          isStylePack: (card.dataset.clearanceV3PackContext || 'deals') === 'style',
          isInActiveStyleResults: !!(this.styleResults && this.styleResults.contains(card)),
          hasAllItems: packProducts.length > 0 && selectedItems.length === packProducts.length,
        }
      })
      const effectivePackLength = this.resolvePackLengthFilter(packStates)

      for (const state of packStates) {
        const { card, packProducts, selectedItems, isStylePack, hasAllItems } = state
        let isVisible = isStylePack
          ? isStyleResultsActive && state.isInActiveStyleResults && hasAllItems
          : isDealsActive && hasAllItems
        if (isVisible && !this.matchesPackLengthFilter(card, effectivePackLength)) {
          isVisible = false
        }
        if (!isStylePack && isVisible) {
          isVisible = visiblePackCount < maxVisibleDealsPacks
          if (isVisible) visiblePackCount += 1
        }

        card.hidden = !isVisible

        const button = card.querySelector('[data-clearance-v3-add-pack]')
        if (button) {
          button.dataset.defaultLabel = this.getPackButtonLabel(
            selectedItems.length,
            packProducts.length
          )
          const hasTransientState =
            button.classList.contains('added') || button.getAttribute('aria-busy') === 'true'
          if (!hasTransientState) {
            button.disabled = selectedItems.length <= 0
            this.updateButtonLabel(button, button.dataset.defaultLabel)
          }
        }

        for (const img of card.querySelectorAll('.pcard-imgs img[data-product-index]')) {
          const product = packProducts[Number(img.dataset.productIndex)]
          img.dataset.available =
            product && this.getVariantForSelectedSize(product) ? 'true' : 'false'
        }
      }

      // Stays visible through the singles fetch even with nothing to show yet: the skeleton is
      // holding those slots, and generated bundles usually land in them. Hiding and reshowing
      // the section around the fetch would reintroduce the shift the skeleton exists to stop.
      if (this.packSection) {
        this.packSection.hidden =
          !isDealsActive || (visiblePackCount <= 0 && !this.isFetchingSingles)
      }

      this.syncPackPlaceholders(visiblePackCount)
      // Cards and placeholders have both settled by here, so the track's scrollable width is
      // final — measure the arrows against it rather than against the pre-filter row.
      if (this.packRow) this.updateScrollArrows(this.packRow)

      // Same two-pass shape as the packs above: availability first, then the Pick & Mix
      // length is resolved from it, then visibility is applied.
      const singleStates = (this.singleCards || []).map((card) => ({
        card,
        hasProducts: this.parseProducts(card, '[data-clearance-v3-single-products]').length > 0,
        isAvailable: this.hasSelectableItems(
          card,
          '[data-clearance-v3-single-products]',
          false
        ),
        isStyleCard: (card.dataset.clearanceV3CardContext || 'deals') === 'style',
      }))
      const effectiveSingleLength = this.resolveSingleLengthFilter(singleStates)

      // Counts every Pick & Mix card that passes the filter, including ones held back by
      // the page cap — drives the item count, the empty state, and whether more remain.
      let matchingSingleCount = 0

      for (const state of singleStates) {
        const { card, hasProducts, isAvailable, isStyleCard } = state
        // Only Pick & Mix gets the Regular fallback; style rows keep filtering on the raw
        // selection so they stay in step with the category tiles below, which decide their
        // own visibility from productMatchesTopLengthFilter.
        const matchesLength = isStyleCard
          ? this.matchesTopLengthFilter(card)
          : this.matchesTopLengthFilter(card, effectiveSingleLength)
        let isVisible = isStyleCard
          ? isStyleResultsActive && hasProducts && matchesLength
          : isDealsActive && hasProducts && matchesLength

        // Unavailable cards are excluded by CSS (.card[data-size-available='false']), so
        // the cap tracks the same set the shopper actually sees.
        if (!isStyleCard && isVisible && isAvailable) {
          matchingSingleCount += 1
          if (matchingSingleCount > this.singlesShownCount) isVisible = false
        }

        card.hidden = !isVisible
        card.dataset.sizeAvailable = isAvailable ? 'true' : 'false'

        const sizeElement = card.querySelector('[data-clearance-v3-card-size]')
        const cardSizeLabel = this.getCardSizeLabel(card)
        if (sizeElement) this.renderCardSizeLabel(sizeElement, cardSizeLabel)

        const button = card.querySelector('[data-clearance-v3-add-single]')
        if (button) {
          const hasTransientState =
            button.classList.contains('added') || button.getAttribute('aria-busy') === 'true'
          if (!hasTransientState) {
            button.disabled = !isAvailable
            this.updateButtonLabel(
              button,
              isAvailable
                ? button.dataset.defaultLabel || 'Add'
                : cardSizeLabel
                  ? `Unavailable in ${cardSizeLabel}`
                  : 'Unavailable'
            )
          }
        }

        if (!isStyleCard && isVisible && isAvailable) visibleSingleCount += 1
      }

      // Skeleton stands in for the grid until the fetch lands.
      if (this.singlesSkeleton) {
        this.singlesSkeleton.hidden = !(isDealsActive && this.isFetchingSingles)
      }

      // Reports the full matching total, not just the revealed page — "30 items" would be
      // misleading when the filter matched 200. Stays hidden while fetching and at zero, so
      // "0 items" never shows; the empty state speaks for that case instead.
      if (this.singlesCountElement) {
        const showSinglesCount = !this.isFetchingSingles && matchingSingleCount > 0
        this.singlesCountElement.hidden = !showSinglesCount
        if (showSinglesCount) {
          this.singlesCountElement.textContent = `${matchingSingleCount} ${
            matchingSingleCount === 1 ? 'item' : 'items'
          }`
        }
      }

      // Empty state for the singles grid specifically — shows when nothing matched even
      // if packs are still visible above. Suppressed while the Storefront fetch is in
      // flight so it can't flash before the grid has been populated.
      if (this.emptyElement) {
        this.emptyElement.hidden =
          !isDealsActive || this.isFetchingSingles || matchingSingleCount > 0
      }

      // Sentinel drives the next reveal, so it only exists while pages remain. Reveals are
      // synchronous (the cards are already in the DOM), so no loader is shown for them.
      this.hasMoreSingles = matchingSingleCount > visibleSingleCount
      if (this.singlesSentinel) {
        this.singlesSentinel.hidden = !(isDealsActive && this.hasMoreSingles)
      }

      this.updateStyleRowCounts()

      const allSingles = !this.isFetchingSingles ? this.parseAllProducts()?.singles || [] : null
      // Generated rows must exist before the tile loop, or the catch-all tile (and any tile
      // only fallback rows can fill) would be judged empty.
      if (allSingles !== null) {
        this.syncStyleFallbackRows(allSingles)
        this.renderStyleFallbackReport()
      }
      for (const card of this.styleCards || []) {
        if (!this.styleView?.contains(card)) continue
        const handle = card.dataset.categoryHandle || ''
        if (this.isBottomsCategory(handle)) {
          card.classList.toggle('needs-unlock', !this.selectedBottomWaist)
        }
        if (allSingles !== null) {
          const result = this.styleResults?.querySelector(`[data-category-handle="${handle}"]`)
          if (result) {
            let hasAvailable = false
            for (const subRow of result.querySelectorAll('[data-clearance-v3-style-row]')) {
              const pdpAppId = subRow.dataset.pdpAppId || ''
              if (!pdpAppId) continue
              if (
                allSingles.some(
                  (p) =>
                    this.isProductInStyleRow(p, subRow) &&
                    this.getVariantForSelectedSize(p) &&
                    this.productMatchesTopLengthFilter(p)
                )
              ) {
                hasAvailable = true
                break
              }
            }
            card.hidden = !hasAvailable
          }
        }
      }
    }

    getPackButtonLabel(availableCount, totalCount) {
      if (availableCount <= 0) return 'Unavailable'
      if (availableCount >= totalCount) return `Add All ${totalCount}`
      return 'Unavailable'
    }

    updatePackPricing() {
      for (const card of this.packCards || []) {
        const selectedItems = this.getSelectableItems(
          card,
          '[data-clearance-v3-pack-products]',
          true
        )
        const basePrice = selectedItems.length
          ? selectedItems.reduce((total, item) => total + this.getItemPrice(item), 0)
          : Number(card.dataset.basePrice || 0)
        const packCount = selectedItems.length || Number(card.dataset.packCount || 0)
        if (!basePrice || !packCount) continue

        const discount = this.getDiscountForCount(this.clearanceCount + packCount, card)
        // Discount each unit individually (Shopify "each" allocation) so the pack total matches the cart.
        // In tag mode the pack's items can be tagged differently, so each one resolves its own rate.
        const discountedPrice = selectedItems.length
          ? selectedItems.reduce(
              (total, item) =>
                total +
                this.getDiscountedPrice(
                  this.getItemPrice(item),
                  this.getDiscountForItem(item, this.clearanceCount + packCount)
                ),
              0
            )
          : this.getDiscountedPrice(basePrice, discount)
        const perItemPrice = Math.round(discountedPrice / packCount)
        const priceElement = card.querySelector('[data-clearance-v3-pack-price]')
        const wasElement = card.querySelector('[data-clearance-v3-pack-was]')
        const discountElement = card.querySelector('[data-clearance-v3-pack-discount]')
        const perElement = card.querySelector('[data-clearance-v3-pack-per]')

        if (priceElement) priceElement.textContent = this.formatMoney(discountedPrice)
        this.updatePackPerPrice(
          perElement,
          this.formatMoney(perItemPrice),
          card.dataset.clearanceV3UnitTerm
        )

        if (wasElement) {
          wasElement.hidden = discount <= 0
          wasElement.textContent = this.formatMoney(basePrice)
        }

        if (discountElement) {
          // Mixed rates across a tag-mode pack have no single headline number,
          // so fall back to the savings actually applied.
          const packDiscount = basePrice
            ? Math.round(((basePrice - discountedPrice) / basePrice) * 100)
            : discount
          discountElement.hidden = packDiscount <= 0
          discountElement.textContent = `${packDiscount}% OFF`
        }
      }
    }

    updateSinglePricing() {
      for (const card of this.singleCards || []) {
        // Tag mode prices from the discount tags alone, so a card can be
        // clearance-priced without the Clearance tag. Quantity mode still needs
        // the Clearance tag, because its tiers are not tag-driven.
        const isClearancePriced = isClearanceTagModeSafe()
          ? card.dataset.clearanceSelectTagged === 'true' ||
            card.dataset.clearanceBaseTagged === 'true'
          : card.dataset.clearanceTagged === 'true'
        const selectedItems = this.getSelectableItems(
          card,
          '[data-clearance-v3-single-products]',
          false
        )
        const itemCount = selectedItems.length || 1
        const basePrice = selectedItems.length
          ? this.getItemPrice(selectedItems[0])
          : Number(card.dataset.basePrice || 0)
        if (!basePrice) continue

        const priceElement = card.querySelector('[data-clearance-v3-card-price]')

        if (isClearancePriced) {
          const predictedDiscount = this.getDiscountForCount(this.clearanceCount + itemCount, card)
          const discountedPrice = this.getDiscountedPrice(basePrice, predictedDiscount)
          const wasElement = card.querySelector('[data-clearance-v3-clearance-was]')
          const discElement = card.querySelector('[data-clearance-v3-clearance-disc]')

          // The clearance rate comes off the product's price, but the shopper is
          // saving against the compare-at price — so that is what gets struck
          // through, and the badge is the total saving between the two prices
          // actually on the card rather than the clearance rate alone.
          const compareAtPrice = selectedItems.length
            ? this.getItemCompareAtPrice(selectedItems[0])
            : Number(card.dataset.clearanceCompareAtPrice || 0)
          const wasPrice = Math.max(compareAtPrice || 0, basePrice)
          const totalDiscount =
            wasPrice > discountedPrice
              ? Math.round(((wasPrice - discountedPrice) / wasPrice) * 100)
              : predictedDiscount

          card.dataset.discountActive = predictedDiscount > 0 ? 'true' : 'false'
          if (priceElement) priceElement.textContent = this.formatMoney(discountedPrice)
          if (wasElement) {
            wasElement.hidden = predictedDiscount <= 0
            wasElement.textContent = this.formatMoney(wasPrice)
          }
          if (discElement) {
            discElement.hidden = predictedDiscount <= 0
            discElement.textContent = `${totalDiscount}% OFF`
          }
        } else {
          card.dataset.discountActive = 'false'
          if (priceElement) priceElement.textContent = this.formatMoney(basePrice)
        }
      }
    }

    getItemPrice(item) {
      return Number(item?.variant?.price || item?.product?.price || 0)
    }

    // The item's compare-at price, falling back to the product's and then to 0
    // for a product that carries no compare-at price at all.
    getItemCompareAtPrice(item) {
      return Number(item?.variant?.compareAtPrice || item?.product?.compareAtPrice || 0)
    }

    // Plain-text form of the shared savings line, for surfaces that take text
    // rather than HTML (the toast).
    getTagModeSavingsText() {
      const html = window.TC_CLEARANCE_CONFIG?.savingsMessageHtml || ''
      const el = document.createElement('div')
      el.innerHTML = html
      return el.textContent.trim()
    }

    getToastSubtitle() {
      if (isClearanceTagModeSafe()) return this.getTagModeSavingsText()
      const nextTier = this.getNextTier(this.clearanceCount)
      if (!nextTier) return 'All savings unlocked'
      const needed = nextTier.quantity - this.clearanceCount
      const itemLabel = needed === 1 ? 'item' : 'items'
      return `${needed} more ${itemLabel} for ${nextTier.percentage}% OFF`
    }

    getToastContainer() {
      let wrap = document.getElementById('c3-toast-wrap')
      if (!wrap) {
        wrap = document.createElement('div')
        wrap.id = 'c3-toast-wrap'
        wrap.className = 'c3-toast-wrap'
        document.body.appendChild(wrap)
      }

      let status = wrap.querySelector('[data-clearance-v3-toast-status]')
      if (!status) {
        status = this.createSrOnlySpan('')
        status.dataset.clearanceV3ToastStatus = ''
        status.setAttribute('role', 'status')
        status.setAttribute('aria-live', 'polite')
        status.setAttribute('aria-atomic', 'true')
        wrap.prepend(status)
      }

      return wrap
    }

    showToast(title, subtitle, isTierUp, isMaxTier) {
      const wrap = this.getToastContainer()
      const status = wrap.querySelector('[data-clearance-v3-toast-status]')
      const toast = document.createElement('button')
      toast.type = 'button'
      toast.className = `toast${isTierUp ? ' tier-up' : ''}${isMaxTier ? ' max-tier' : ''}`
      toast.setAttribute('aria-label', `${title}. ${subtitle}. Open cart.`)

      const icon = document.createElement('span')
      icon.className = 'toast-icon'
      icon.setAttribute('aria-hidden', 'true')
      icon.textContent = isTierUp ? '🎉' : '✓'

      const content = document.createElement('span')
      const titleElement = document.createElement('span')
      titleElement.className = 'toast-title'
      titleElement.textContent = title
      const subtitleElement = document.createElement('span')
      subtitleElement.className = 'toast-sub'
      subtitleElement.textContent = subtitle
      content.append(titleElement, subtitleElement)
      toast.append(icon, content)

      toast.addEventListener('click', () => this.openCartDrawer())
      wrap.appendChild(toast)
      if (status) {
        status.textContent = ''
        requestAnimationFrame(() => {
          status.textContent = `${title}. ${subtitle}`
        })
      }
      requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('show')))

      const removeToast = () => {
        if (document.activeElement === toast || toast.matches(':hover')) {
          window.setTimeout(removeToast, 1000)
          return
        }
        toast.classList.remove('show')
        window.setTimeout(() => toast.remove(), 350)
      }
      window.setTimeout(removeToast, isTierUp ? 5000 : 4000)
    }

    async addItemsToCart(items, button, card, source) {
      if (!items.length) {
        this.setTemporaryButtonLabel(button, 'Unavailable')
        return
      }

      this.setButtonLoading(button)
      document.dispatchEvent(
        new CustomEvent('clearanceatcstart', {
          bubbles: true,
          detail: { quantity: items.length },
        })
      )

      try {
        const cartItems = []
        for (const item of items) {
          const id = item.variant.id
          const existingItem = cartItems.find((cartItem) => cartItem.id === id)
          if (existingItem) {
            existingItem.quantity += 1
            continue
          }

          cartItems.push({
            id,
            quantity: 1,
            properties: {
              _clearance: 'true',
              _clearance_size: this.selectedTopSize,
              _clearance_source: source,
            },
          })
        }

        const response = await fetch(`${this.routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: cartItems,
          }),
        })
        const data = await response.json()

        if (!response.ok || data.status) {
          throw new Error(data.message || 'Unable to add clearance items')
        }

        card?.classList.add('in-cart')
        this.setButtonAdded(button)
        this._pendingAddTitle = items[0]?.product?.title || items[0]?.product?.color || 'Added!'
        const variantId = items[0]?.variant?.id || null

        document.dispatchEvent(
          new CustomEvent('clearanceatcsuccess', {
            bubbles: true,
            detail: {
              quantity: items.length,
              variantId,
            },
          })
        )
        document.dispatchEvent(
          new CustomEvent('cartaddsuccess', {
            bubbles: true,
            detail: {
              openCart: false,
              refreshCart: true,
              variantId,
            },
          })
        )

        await this.refreshCartSummary()
      } catch (error) {
        console.error('Clearance V3 add failed', error)
        this.setTemporaryButtonLabel(button, 'Unable to add')
      } finally {
        window.setTimeout(() => {
          this.restoreButton(button)
        }, 1400)
      }
    }

    setButtonLoading(button) {
      if (!button) return

      button.disabled = true
      button.setAttribute('aria-busy', 'true')
      this.updateButtonLabel(button, 'Adding...')
    }

    updateButtonLabel(button, label) {
      if (!button) return

      const normalizedLabel = String(label || '').trim()
      const productTitle = String(button.dataset.productTitle || '').trim()
      const accessibleName = [normalizedLabel, productTitle].filter(Boolean).join(' ')
      const inCartStatus = button.dataset.inCart === 'true' ? ' Currently in cart.' : ''

      button.textContent = normalizedLabel
      button.setAttribute('aria-label', `${accessibleName}.${inCartStatus}`.trim())
    }

    createSrOnlySpan(text) {
      const span = document.createElement('span')
      span.className = 'visually-hidden'
      span.textContent = text
      return span
    }

    setButtonAdded(button) {
      if (!button) return

      button.classList.add('added')
      button.removeAttribute('aria-busy')
      this.updateButtonLabel(button, button.dataset.addedLabel || 'Added')
    }

    setTemporaryButtonLabel(button, label) {
      if (!button) return

      button.disabled = true
      button.removeAttribute('aria-busy')
      this.updateButtonLabel(button, label)

      window.setTimeout(() => {
        this.restoreButton(button)
      }, 1400)
    }

    restoreButton(button) {
      if (!button) return

      button.disabled = false
      button.removeAttribute('aria-busy')
      button.classList.remove('added')
      this.updateButtonLabel(button, button.dataset.defaultLabel || 'Add')
    }

    async refreshCartSummary() {
      const summary = await this.fetchClearanceSummary()
      this.updateTierStrip(summary)
      this.syncCardCartState(summary.variantIds, summary.variantQuantities)
      this.updateBrowse()
    }

    async fetchClearanceSummary() {
      try {
        const response = await fetch(`${this.routesRoot()}cart.js`, {
          headers: { Accept: 'application/json' },
        })
        const cart = await response.json()
        let count = 0
        let subtotal = 0
        let originalSubtotal = 0
        const variantIds = new Set()
        const variantQuantities = new Map()

        const singlesProductIds = new Set(
          (this.parseAllProducts()?.singles || []).map((p) => Number(p.productId))
        )

        for (const item of cart.items || []) {
          const isClearanceTaggedVariant =
            this.clearanceVariantIds?.size > 0 &&
            this.clearanceVariantIds.has(Number(item.variant_id || item.id || 0))
          const isClearanceFlowItem = item.properties?._clearance === 'true'
          if (!isClearanceTaggedVariant && !isClearanceFlowItem) continue

          count += Number(item.quantity || 0)
          subtotal += Number(item.final_line_price || item.line_price || 0)
          originalSubtotal += Number(item.original_line_price || item.line_price || 0)

          const variantId = Number(item.variant_id || item.id || 0)
          if (variantId) {
            variantIds.add(variantId)
            variantQuantities.set(
              variantId,
              (variantQuantities.get(variantId) || 0) + Number(item.quantity || 0)
            )
          }
        }

        return { count, subtotal, originalSubtotal, variantIds, variantQuantities }
      } catch (error) {
        return {
          count: this.clearanceCount,
          subtotal: this.clearanceSubtotal,
          variantIds: this.cartVariantIds,
          variantQuantities: this.cartVariantQuantities,
        }
      }
    }

    syncCardCartState(variantIds, variantQuantities = null) {
      this.cartVariantIds = variantIds instanceof Set ? variantIds : new Set()
      this.cartVariantQuantities =
        variantQuantities instanceof Map ? variantQuantities : new Map()

      for (const card of this.singleCards || []) {
        const items = this.getSelectableItems(card, '[data-clearance-v3-single-products]', false)
        const isInCart = items.some((item) => this.cartVariantIds.has(Number(item.variant.id)))
        card.classList.toggle('in-cart', isInCart)
        this.syncCardInCartText(card, '[data-clearance-v3-add-single]', isInCart)
      }

      for (const card of this.packCards || []) {
        const items = this.getSelectableItems(card, '[data-clearance-v3-pack-products]', true)
        const requiredVariantQuantities = new Map()
        for (const item of items) {
          const variantId = Number(item.variant.id)
          requiredVariantQuantities.set(
            variantId,
            (requiredVariantQuantities.get(variantId) || 0) + 1
          )
        }
        let isInCart = items.length > 0
        for (const [variantId, requiredQuantity] of requiredVariantQuantities) {
          const cartQuantity =
            this.cartVariantQuantities.get(variantId) ||
            (this.cartVariantIds.has(variantId) ? 1 : 0)
          if (cartQuantity < requiredQuantity) {
            isInCart = false
            break
          }
        }
        card.classList.toggle('in-cart', isInCart)
        this.syncCardInCartText(card, '[data-clearance-v3-add-pack]', isInCart)
      }
    }

    syncCardInCartText(card, buttonSelector, isInCart) {
      const button = card.querySelector(buttonSelector)
      if (!button) return

      button.dataset.inCart = isInCart ? 'true' : 'false'
      this.updateButtonLabel(button, button.textContent || button.dataset.defaultLabel || 'Add')
    }

    handleQuickAddSuccess(event) {
      if (event.detail?.source !== 'clearance-v3-quick-add') return

      const productId = String(event.detail.productId || '')
      if (productId) {
        for (const card of this.singleCards || []) {
          if (String(card.dataset.productId) === productId) card.classList.add('in-cart')
        }
      }

      this._pendingAddTitle = event.detail.productTitle || 'Added!'
      this.refreshCartSummary()
    }

    updateTierStrip(summary) {
      const previousCount = this.clearanceCount ?? 0
      const previousTier = this.getCurrentTier(previousCount)
      this.clearanceCount = Number(summary.count || 0)
      this.clearanceSubtotal = Number(summary.subtotal || 0)

      const currentTier = this.getCurrentTier(this.clearanceCount)
      const nextTier = this.getNextTier(this.clearanceCount)
      if (currentTier && currentTier !== previousTier) {
        const isMaxTier = !nextTier
        this._pendingAddTitle = null
        this.showToast(
          isMaxTier ? 'All savings unlocked' : `${currentTier.percentage}% Off Unlocked!`,
          `${this.clearanceCount} items in bundle`,
          true,
          isMaxTier
        )
      } else if (this._pendingAddTitle) {
        const title = this._pendingAddTitle
        this._pendingAddTitle = null
        this.showToast(title, this.getToastSubtitle(), false)
      }

      if (this.countElement) {
        this.countElement.textContent = this.clearanceCount > 0 ? String(this.clearanceCount) : ''
      }

      // Keep the cart button's accessible name in sync with the item count,
      // since the visible content is only a bag icon + number.
      if (this.viewCartButton) {
        const itemLabel = this.clearanceCount === 1 ? 'item' : 'items'
        this.viewCartButton.setAttribute(
          'aria-label',
          this.clearanceCount > 0 ? `Open cart, ${this.clearanceCount} ${itemLabel}` : 'Open cart'
        )
      }

      if (this.messageElement && isClearanceTagModeSafe()) {
        // Tag mode has no tiers to progress through — show the fixed savings line.
        this.messageElement.innerHTML = window.TC_CLEARANCE_CONFIG?.savingsMessageHtml || ''
      } else if (this.messageElement) {
        if (nextTier) {
          const needed = nextTier.quantity - this.clearanceCount
          const itemLabel = needed === 1 ? 'item' : 'items'
          // Only the "Add..." call-to-action is emphasized (bold/orange); the
          // "XX% OFF" status stays default and "(Saving $XX)" is bold/green.
          const cta = `<span class="tier-msg__cta">Add ${needed} ${itemLabel} for ${nextTier.percentage}% OFF</span>`
          if (currentTier) {
            const status = `<span class="tier-msg__status">${currentTier.percentage}% OFF</span>`
            const originalSubtotal = Number(
              summary.originalSubtotal || this.clearanceSubtotal || 0
            )
            const estimatedSavings = Math.round((originalSubtotal * currentTier.percentage) / 100)
            const saving =
              estimatedSavings > 0
                ? `<span class="tier-saved">(Saving ${this.formatMoney(estimatedSavings)})</span>`
                : ''
            this.messageElement.innerHTML = `${status}${saving}${cta}`
          } else {
            this.messageElement.innerHTML = cta
          }
        } else if (currentTier) {
          this.messageElement.textContent = 'All savings unlocked'
        } else {
          this.messageElement.textContent = ''
        }
      }

      this.updateStickyBarFill()
    }

    // Overall progress toward the top tier, mirroring the clearance-banner
    // sticky bar fill.
    updateStickyBarFill() {
      if (!this.stickyFill) return
      if (isClearanceTagModeSafe()) {
        this.stickyBar?.setAttribute('hidden', '')
        return
      }
      if (!this.tiers.length) return
      const maxQuantity = this.tiers[this.tiers.length - 1].quantity || 1
      const percent = Math.min(100, Math.round((this.clearanceCount / maxQuantity) * 100))
      this.stickyFill.style.width = `${percent}%`
      this.stickyBar?.setAttribute('aria-valuenow', String(percent))
    }

    getCurrentTier(count) {
      let currentTier = null
      for (const tier of this.tiers) {
        if (count >= tier.quantity) currentTier = tier
      }
      return currentTier
    }

    getNextTier(count) {
      return this.tiers.find((tier) => count < tier.quantity) || null
    }

    // `card` is only consulted in tag mode, where the discount is fixed by the
    // product's own tags and `count` is irrelevant.
    getDiscountForCount(count, card) {
      if (isClearanceTagModeSafe()) {
        return resolveClearanceDiscount({
          hasSelectTag: card?.dataset.clearanceSelectTagged === 'true',
          hasBaseTag: card?.dataset.clearanceBaseTagged === 'true',
        })
      }
      const tier = this.getCurrentTier(count)
      return tier ? tier.percentage : this.baseDiscount
    }

    // Per-item discount for packs, whose items can be tagged differently from
    // each other in tag mode.
    getDiscountForItem(item, count) {
      if (!isClearanceTagModeSafe()) return this.getDiscountForCount(count)
      return resolveClearanceDiscount({
        hasSelectTag: !!item?.product?.selectTagged,
        hasBaseTag: !!item?.product?.baseTagged,
      })
    }

    // Mirrors Shopify's per-unit "each" allocation: the discount is truncated
    // (floored) to the cent, then subtracted. Keeps card prices matching the cart.
    getDiscountedPrice(price, percentage) {
      const discountAmount = Math.floor((price * percentage) / 100)
      return price - discountAmount
    }

    // Recovers the active market's currency format from data-money-probe — a price the
    // section rendered through Liquid's own `money` filter. Because the probe's cents
    // amount is also known, the digit count alone says whether the market shows a minor
    // unit, and the remaining characters give the symbol placement and separators.
    // Deliberately not window.Shopify.formatMoney: that is pinned to the USD
    // `${{amount}}` format in global.js, so it prints "$" on every market. Memoized —
    // the probe cannot change without a re-render.
    moneyParts() {
      if (this.parsedMoneyParts) return this.parsedMoneyParts

      const probe = this.dataset.moneyProbe || ''
      const probeCents = Number(this.dataset.moneyProbeCents)
      const firstDigit = probe.search(/\d/)
      const lastDigit = probe.length - 1 - probe.split('').reverse().join('').search(/\d/)

      // USD default, matching the previous behaviour if the probe is missing or unparseable.
      this.parsedMoneyParts = { prefix: '$', suffix: '', thousands: ',', decimal: '.', precision: 2 }

      if (!Number.isFinite(probeCents) || probeCents <= 0 || firstDigit < 0 || lastDigit < firstDigit) {
        return this.parsedMoneyParts
      }

      const core = probe.slice(firstDigit, lastDigit + 1)
      const precision = core.replace(/\D/g, '').length - String(Math.round(probeCents / 100)).length
      if (precision !== 0 && precision !== 2) return this.parsedMoneyParts

      // With a minor unit, the last two digits are always preceded by the decimal
      // separator, which leaves the grouping separator as the last non-digit before it.
      const decimal = precision === 2 ? core.charAt(core.length - 3) : ''
      if (precision === 2 && (!decimal || /\d/.test(decimal))) return this.parsedMoneyParts

      const whole = precision === 2 ? core.slice(0, core.length - 3) : core
      const separators = whole.replace(/\d/g, '')

      this.parsedMoneyParts = {
        prefix: probe.slice(0, firstDigit),
        suffix: probe.slice(lastDigit + 1),
        thousands: separators ? separators.charAt(separators.length - 1) : '',
        decimal: decimal,
        precision: precision,
      }

      return this.parsedMoneyParts
    }

    // Mirrors the Liquid `{% render 'money-prefix' %}{{ value | money }}` output used by
    // the server-rendered cards, so client-side repricing keeps the market's currency.
    // A zero minor unit is dropped (as before) by omitting it rather than by matching a
    // trailing ".00" — formats such as `${{amount}} CAD` and `{{amount}} €` end in a suffix.
    formatMoney(amountInCents) {
      const amount = Number(amountInCents)
      if (!Number.isFinite(amount)) return ''

      const parts = this.moneyParts()
      const [whole, minor] = (amount / 100).toFixed(parts.precision).split('.')
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${parts.thousands}`)
      const value = minor && minor !== '00' ? grouped + parts.decimal + minor : grouped

      return (this.dataset.moneyPrefix || '') + parts.prefix + value + parts.suffix
    }

    routesRoot() {
      return window.Shopify?.routes?.root || '/'
    }
  }

  customElements.define('clearance-v3-flow', ClearanceV3Flow)
}
